import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Mail,
  Send,
  Building2,
  Users,
  X,
  ChevronDown,
  MapPin,
  RefreshCw,
  Pencil,
  Check,
  Clock,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../styles/StartProjectModal.css';
import {
  injectNameIntoScript,
  matchLeadingGreetingHead,
  normalizeNamePlaceholders,
  normalizeScriptWithPlaceholder,
  parseLeadingGreetingBoundary,
} from '../utils/emailPreview';
import {
  CAMPAIGN_GREETING_SKELETON,
  findInvalidRequiredCampaignScriptIndexes,
  getCampaignMessageBodyCaretOffset,
  hasCampaignMessageBody,
  normalizeCampaignScriptsForLaunch,
  prepareUploadedCampaignScripts,
  selectUploadedCampaignScripts,
} from '../utils/campaignComposer';
import { deriveAskFields, deriveCampaignProperties } from '../utils/campaignAskFields';
import { buildCampaignPropertySubject } from '../utils/campaignSubject';
import {
  deriveNonRequestableFields,
  describeNonRequestableRequests,
} from '../utils/nonRequestableFields';
import { buildCampaignDraftForbiddenValues } from '../utils/campaignDraftScope';
import { buildDefaultFollowUpMessages, buildFollowUpConfig } from '../utils/campaignFollowUps';
import { validateCampaignLaunchDraft } from '../utils/draftGuards';
import {
  buildContactAwareRecipientPlan,
  buildRecipientVariableReviews,
  findBlockingRecipientVariableIssue
} from '../utils/recipientVariableReview';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useSessionStorage } from '../contexts/SessionStorageContext';
import { buildActiveSignatureFromProfile } from '../utils/signatureBuilder';
import SignaturePreview from './SignaturePreview';
import HighlightedTextarea from './HighlightedTextarea';
import { setEditorCaret } from '../utils/editorCaret';
import { MAILBOX_NOT_CONNECTED_MESSAGE } from '../utils/mailboxConnection';
import { ConnectMailboxButton } from './MailboxConnectionBanner';

// Combined-per-broker send mode is PARKED (gated OFF live). Combining a broker's
// properties into one thread requires a pipeline-wide rebuild of inbound
// extraction / sheet write-back / completion / reply-routing (all assume one
// thread = one property = one row) plus dashboard conversation grouping. See the
// backlog. Flip this to true ONLY after that's built + verified in a dev env.
const COMBINE_MODE_ENABLED = false;
const DEFAULT_FOLLOWUP_MESSAGES = buildDefaultFollowUpMessages();
const LAUNCH_PREFLIGHT_DEBOUNCE_MS = 500;
const LAUNCH_PREFLIGHT_PENDING_BLOCKER = {
  key: 'preflight:pending',
  message: 'Checking launch safety. Start Campaign stays disabled until this check finishes.'
};
// A campaign launched into an unlinked mailbox writes its outbox and nothing
// ever sends, so this is a launch blocker like any other -- it only stops the
// final Start control, and everything before it keeps working.
const MAILBOX_NOT_CONNECTED_BLOCKER = {
  key: 'mailbox:not-connected',
  message: MAILBOX_NOT_CONNECTED_MESSAGE
};
const GENERATED_GREETING_BOUNDARY_BLOCKER = {
  key: 'generation:greeting-boundary',
  message: 'SiteSift did not receive a complete verified generated message contract. Nothing was replaced. Generate again or edit the existing message manually.'
};
export const GENERATED_MESSAGE_CONTRACT = 'sitesift-body-v1';
export const SECONDARY_GENERATION_RUNNING_MESSAGE = 'Generating the remaining property messages\u2026';
export const SECONDARY_GENERATION_FAILED_MESSAGE = 'Could not generate the remaining property messages. Use Regenerate All to try again.';

/**
 * The half of a generation failure worth showing an advisor.
 *
 * A failed-precondition is the server refusing the message and saying how to
 * fix it, so it is kept whatever shape it takes. Anything else is kept only
 * when it reads as a sentence; a transport failure carries no advice, and
 * pasting its code into the launch panel just reads as noise
 * ("...Use Regenerate All to try again. internal").
 */
export function describeGenerationFailure(error) {
  const message = String(error?.message || '').trim();
  if (!message) return '';
  if (/failed-precondition/.test(String(error?.code || ''))) return message;
  return /\s/u.test(message) && /[.!?]$/u.test(message) ? message : '';
}
const GENERATED_BODY_LEADING_GREETING_PATTERN = /^(?:good\s+(?:morning|afternoon|evening|day)|greetings|hello|dear|hey|hi|morning|afternoon|evening)(?=[\s,!?:;.–—[{]|$)/iu;
const GENERATED_BODY_ATTACHED_GREETING_RECIPIENT_PATTERN = /^(?:good\s+(?:morning|afternoon|evening|day)|greetings|hello|dear|hey|hi|morning|afternoon|evening)(?:-|\/)[\p{L}\p{M}][\p{L}\p{M}'’.-]{0,79}[,!?:;](?=\s|$)/iu;
const GENERATED_BODY_ATTACHED_GREETING_PREFIX_PATTERN = /^(?:good\s+(?:morning|afternoon|evening|day)|greetings|hello|dear|hey|hi|morning|afternoon|evening)(?:-|\/)/iu;
const GENERATED_BODY_MULTIWORD_ATTACHED_RECIPIENT_PATTERN = /^(?:\p{Lu}[\p{L}\p{M}'’.-]{0,79})(?:[\t ]+\p{Lu}[\p{L}\p{M}'’.-]{0,79}){1,5}[,!?:;](?=\s|$)/u;
const GENERATED_BODY_PLACEHOLDER_PATTERN = /\[[^\]\n]{1,80}\]|\{\{?[^}\n]{1,80}\}?\}|<[^>\n]{1,80}>/u;
const GENERATED_BODY_INCOMPLETE_PLACEHOLDER_PATTERN = /(?:\[\s*|\{\{?\s*|<\s*)[\p{L}_][^\r\n\]}>]{0,79}(?=$|\r?\n)/u;
const GENERATED_BODY_UNICODE_PLACEHOLDER_PATTERN = /［[^\n］]{1,80}］|【[^\n】]{1,80}】|〖[^\n〗]{1,80}〗|〘[^\n〙]{1,80}〙|〚[^\n〛]{1,80}〛|⟦[^\n⟧]{1,80}⟧|⟨[^\n⟩]{1,80}⟩|〈[^\n〉]{1,80}〉|《[^\n》]{1,80}》|「[^\n」]{1,80}」|『[^\n』]{1,80}』|〔[^\n〕]{1,80}〕|﹙[^\n﹚]{1,80}﹚|﹛[^\n﹜]{1,80}﹜|﹝[^\n﹞]{1,80}﹞|｛[^\n｝]{1,80}｝|＜[^\n＞]{1,80}＞|（[^\n）]{1,80}）/u;
const GENERATED_BODY_TOKEN_PLACEHOLDER_PATTERN = /(?:[%％][\p{L}\p{M}_][\p{L}\p{M}\p{N}_-]{0,79}[%％]|[@＠][\p{L}\p{M}_][\p{L}\p{M}\p{N}_-]{0,79}[@＠]|(?:__|＿＿)[\p{L}\p{M}_][\p{L}\p{M}\p{N}_-]{0,79}(?:__|＿＿)|[$＄][\p{L}\p{M}_][\p{L}\p{M}\p{N}_-]{0,79})/u;
const GENERATED_BODY_REPEATED_PUNCTUATION_PLACEHOLDER_PATTERN = /(?:(?:\(\(|<<|##|~~|\|\||::)[\p{L}\p{M}_][\p{L}\p{M}\p{N}_ -]{0,79}(?:\)\)|>>|##|~~|\|\||::))/u;
const GENERATED_BODY_VALEDICTION_SOURCE = String.raw`appreciatively|best(?:\s+regards)?|cheers|cordially|faithfully|fondly|gratefully|kindly|regards|respectfully|sincerely|thank(?:s|\s+you)(?:\s+again)?|truly|warmly|yours\s+(?:faithfully|respectfully|sincerely|truly|warmly)|very\s+truly\s+yours|(?:with\s+)?(?:best|kind|kindest|many|sincere|warm|warmest)\s+(?:appreciation|gratitude|regards|thanks|wishes)|with\s+(?:appreciation|gratitude|regards|thanks|wishes)`;
const GENERATED_BODY_TERMINAL_SIGNOFF_PATTERN = new RegExp(
  `(?:^|\\n)\\s*(?:${GENERATED_BODY_VALEDICTION_SOURCE})[,.!?:;]?\\s*$`,
  'iu'
);
const GENERATED_BODY_VALEDICTION_LINE_PATTERN = new RegExp(
  `^(?:${GENERATED_BODY_VALEDICTION_SOURCE})[,.!?:;]?$`,
  'iu'
);

const GENERATED_SIGNATURE_MARKER_PATTERN = /(?:data-sitesift-professional-signature|sitesift:professional-signature)/i;
const GENERATED_CONTACT_DETAIL_PATTERN = /^(?:[\w.+-]+@[\w.-]+\.[a-z]{2,}|(?:https?:\/\/|www\.)\S+|[a-z0-9](?:[a-z0-9-]{0,62}\.)+[a-z]{2,}(?:\/\S*)?|(?:phone|mobile|cell|tel(?:ephone)?|direct|office|fax)\s*[.:]?\s*(?:\+?\d|\(\d{2,4}\))[\d\s().-]{5,}\d(?:\s*(?:x|ext\.?)\s*\d+)?|[dtmpfoc]\s*[.:]?\s*(?:\+?\d|\(\d{2,4}\))[\d\s().-]{5,}\d(?:\s*(?:x|ext\.?)\s*\d+)?|(?:e|email)\s*[.:]?\s*[\w.+-]+@[\w.-]+\.[a-z]{2,}|(?:w|web|website)\s*[.:]?\s*(?:(?:https?:\/\/|www\.)\S+|[a-z0-9](?:[a-z0-9-]{0,62}\.)+[a-z]{2,}(?:\/\S*)?)|linkedin\s*[.:]?\s*(?:(?:https?:\/\/|www\.)\S+|[a-z0-9](?:[a-z0-9-]{0,62}\.)+[a-z]{2,}(?:\/\S*)?)|(?:\+?\d|\(\d{2,4}\))[\d\s().-]{5,}\d(?:\s*(?:x|ext\.?)\s*\d+)?(?:\s*\((?:cell|direct|mobile|office)\))?)$/i;
const GENERATED_LICENSE_DETAIL_PATTERN = /^(?:(?:lic(?:ense)?s?\.?(?:\s+nos?\.?)?|(?:(?:[a-z]{2}\s+|cal\s*)?dre))\s*(?:[:#]\s*)?)[a-z0-9][a-z0-9\s(),./#-]{1,80}$/i;
const GENERATED_LOCATION_DETAIL_PATTERN = /^[\p{L}][\p{L}\p{M} .'-]{0,48},\s*(?:[A-Z]{2}|[\p{Lu}][\p{Ll}\p{M}.'-]{2,20}(?:\s+[\p{Lu}][\p{Ll}\p{M}.'-]{2,20})?)(?:\s+\d{5}(?:-\d{4})?)?$/u;
const GENERATED_ADDRESS_DETAIL_PATTERN = /^\d{1,8}\s+[\p{L}\p{N}][\p{L}\p{M}\p{N} .'-]{1,72}(?:street|st|avenue|ave|road|rd|boulevard|blvd|drive|dr|lane|ln|way|parkway|pkwy|highway|hwy)\.?(?:,?\s+(?:suite|ste|unit|floor|fl)\s*[\p{L}\p{N}-]+)?(?:,\s*[\p{L}][\p{L}\p{M} .'-]{1,48},\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?)?$/iu;
const GENERATED_CREDENTIAL_DETAIL_PATTERN = /^(?:REALTOR®?|CCIM|SIOR|CCIM\s*,\s*SIOR|MBA|LEED\s+AP|MCR|RPA)$/i;
const GENERATED_PRACTICE_TERM_SOURCE = '(?:accounts?|advisory|analytics|brokerage|capital\\s+markets|debt|equity|finance|industrial|leasing|logistics|office|operations|practice|representation|research|sales|strategy|transactions?|valuation)';
const GENERATED_PRACTICE_MODIFIER_SOURCE = '(?:agency|capital|corporate|debt|global|industrial|landlord|national|office|project|regional|research|strategic|structured|tenant|valuation|workplace)';
const GENERATED_TEAM_DETAIL_PATTERN = new RegExp(
  `^(?:(?:${GENERATED_PRACTICE_MODIFIER_SOURCE})\\s+)*${GENERATED_PRACTICE_TERM_SOURCE}(?:\\s*(?:&|and)\\s*(?:(?:${GENERATED_PRACTICE_MODIFIER_SOURCE})\\s+)*${GENERATED_PRACTICE_TERM_SOURCE})*$`,
  'i'
);
const GENERATED_ACRONYM_ROLE_PATTERN = /^(?:C[A-Z]{1,3}O|[A-Z]?VP)$/i;
const GENERATED_ROLE_NOUN_PATTERN = /^(?:advisor|agent|analyst|associate|broker|chairman|chief|coordinator|director|executive|founder|head|lead|leader|manager|member|officer|owner|partner|president|principal|rep(?:resentative)?|specialist)$/i;
const GENERATED_DEPARTMENT_PATTERN = /^(?:acquisitions?|leasing|operations|sales)$/i;
const GENERATED_FIELD_TERMINAL_PATTERN = /^(?:access|availability|contact|details|metrics|noi|sf)$/i;
const GENERATED_BUSINESS_SIGNAL_PATTERN = /\b(?:associates?|capital|co|commercial|company|corp(?:oration)?|cre|group|holdings?|inc(?:orporated)?|investments?|limited|llc|llp|lp|ltd|partners?|pc|plc|properties|real estate|realty|services|ventures?)\b/i;
const GENERATED_LEGAL_SUFFIX_PATTERN = /\b(?:Co|Corp|Corporation|Inc|Incorporated|Ltd|Limited|LP|LLP|LLC|PLC|PC)\.$/i;
const GENERATED_JOINED_ORGANIZATION_PATTERN = /^[\p{Lu}][\p{L}\p{M}'’.-]*(?:\s+[\p{Lu}][\p{L}\p{M}'’.-]*){0,3}\s+(?:&|and)\s+[\p{Lu}][\p{L}\p{M}'’.-]*(?:\s+[\p{Lu}][\p{L}\p{M}'’.-]*){0,3}$/u;
const GENERATED_BODY_HEADING_PATTERN = /^(?:questions?\s*(?:&|and)\s*answers?|.{1,64}\s+(?:details|improvements|information|overview|steps|summary))$/i;
const GENERATED_PROSE_SIGNAL_PATTERN = /(?:[.!?]$|\b(?:are|available|availability|can|confirm|could|had|has|have|is|matters?|need(?:ed|s)?|please|require(?:d|s)?|should|today|tomorrow|tonight|was|were|would)\b)/i;
const GENERATED_NO_ADDRESSEE_BODY_PATTERN = /^(?:a|am|an|are|can|could|did|do|does|had|has|have|how|i|is|it|may|might|must|my|our|please|shall|should|thank(?:s|\s+you)|the|there|these|they|this|those|today|tomorrow|tonight|we|what|when|where|why|will|would|you|your)\b/i;
const GENERATED_CONTACT_LABEL_PATTERN = /^(?:phone|mobile|cell|tel(?:ephone)?|direct|office|fax)\s*[.:]\s*/i;
const GENERATED_BODY_LIKE_VALEDICTION_PATTERN = /^thank(?:s|\s+you)?\s+again[,!]?$/i;
const GENERATED_WEAK_GREETING_BODY_PROSE_PATTERN = /^(?:morning|afternoon|evening)\s+[^\r\n,;:–—]{0,100}\b(?:aligns?|appears?|are|begins?|can|could|does|fills?|has|have|helps?|is|matters?|needs?|remains?|requires?|should|starts?|was|were|will|works?|would)\b/iu;
const GENERATED_COURTESY_ADVERB_SOURCE = '(?:appreciatively|faithfully|fondly|gratefully|kindly|respectfully|sincerely|truly|warmly)';
const GENERATED_COURTESY_MODIFIER_SOURCE = '(?:best|kind|kindest|many|sincere|warm|warmest)';
const GENERATED_COURTESY_NOUN_SOURCE = '(?:appreciation|gratitude|regards|thanks|wishes)';
const GENERATED_VALEDICTION_PATTERN = new RegExp(
  `^(?:${GENERATED_COURTESY_ADVERB_SOURCE}(?:\\s+yours)?|yours\\s+${GENERATED_COURTESY_ADVERB_SOURCE}|very\\s+${GENERATED_COURTESY_ADVERB_SOURCE}\\s+yours|thank(?:s|\\s+you)?\\s+again|(?:with\\s+)?${GENERATED_COURTESY_MODIFIER_SOURCE}\\s+${GENERATED_COURTESY_NOUN_SOURCE}|with\\s+(?:appreciation|gratitude|regards|thanks|wishes))[,.!?:;]?$`,
  'i'
);
const GENERATED_NAME_CONNECTORS = new Set([
  'al', 'bin', 'da', 'de', 'del', 'della', 'den', 'der', 'di', 'du', 'el', 'la', 'le', 'van', 'von'
]);
const GENERATED_BODY_FIELD_LABELS = new Set([
  'additional details', 'asking rent', 'availability', 'available sf', 'broker contact',
  'cam', 'ceiling ht', 'cre', 'docks', 'drive ins', 'flyer / link', 'flyer link',
  'floor plan', 'floorplan', 'leasing company contact', 'leasing contact', 'listing broker',
  'listing broker contact', 'loading access', 'noi', 'operating expenses', 'power',
  'property details', 'property metrics', 'property owner', 'questions & answers',
  'questions and answers', 'rent', 'rent/sf /yr', 'rent/sf/yr', 'tenant rep', 'total sf'
]);

function normalizeGeneratedBodyFieldLabel(value) {
  return String(value || '')
    .trim()
    .replace(/:$/, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function looksLikeGeneratedBodyFieldLabel(value, bodyFieldLabels = []) {
  const normalized = normalizeGeneratedBodyFieldLabel(value);
  return GENERATED_BODY_FIELD_LABELS.has(normalized)
    || looksLikeConfiguredGeneratedBodyFieldLabel(value, bodyFieldLabels);
}

function looksLikeConfiguredGeneratedBodyFieldLabel(value, bodyFieldLabels = []) {
  const normalized = normalizeGeneratedBodyFieldLabel(value);
  return Array.isArray(bodyFieldLabels) && bodyFieldLabels.some(
    label => normalizeGeneratedBodyFieldLabel(label) === normalized
  );
}

function startsWithGeneratedBodyFieldLabel(value, bodyFieldLabels = []) {
  const text = String(value || '').trimStart();
  return [...text.matchAll(/\s+|[,!?:;.]|[–—]|\s+-\s+/gu)].some(delimiter => (
    delimiter.index > 0
    && looksLikeGeneratedBodyFieldLabel(text.slice(0, delimiter.index), bodyFieldLabels)
  ));
}

function looksLikeGeneratedContactLabelBody(value) {
  const text = String(value || '').trim();
  return GENERATED_CONTACT_LABEL_PATTERN.test(text)
    && !looksLikeGeneratedContactDetail(text)
    && GENERATED_PROSE_SIGNAL_PATTERN.test(text);
}

function looksLikeGeneratedBodyContent(value, bodyFieldLabels = []) {
  const text = String(value || '').trim();
  return looksLikeGeneratedBodyFieldLabel(text, bodyFieldLabels)
    || startsWithGeneratedBodyFieldLabel(text, bodyFieldLabels)
    || looksLikeGeneratedBodyHeading(text)
    || looksLikeGeneratedContactLabelBody(text);
}

export function hasAmbiguousGeneratedWeakGreeting(script) {
  return parseLeadingGreetingBoundary(script).kind === 'ambiguous';
}

function looksLikeGeneratedPersonIdentity(value, bodyFieldLabels = []) {
  const text = String(value || '').trim().replace(/\.$/, '');
  const words = text.split(/\s+/).filter(Boolean);
  if (
    words.length < 1
    || words.length > 6
    || GENERATED_BUSINESS_SIGNAL_PATTERN.test(text)
    || GENERATED_PROSE_SIGNAL_PATTERN.test(text)
    || looksLikeGeneratedBodyFieldLabel(text, bodyFieldLabels)
    || looksLikeGeneratedBodyHeading(text)
    || looksLikeGeneratedRoleDetail(text)
    || looksLikeGeneratedProfessionalDetail(text)
  ) {
    return false;
  }
  return words.every((word, index) => {
    const cleaned = word.replace(/^[^\p{L}]+|[^\p{L}\p{M}.'’–-]+$/gu, '');
    if (!cleaned) return false;
    if (index > 0 && GENERATED_NAME_CONNECTORS.has(cleaned.toLowerCase())) return true;
    return /^\p{Lu}/u.test(cleaned);
  });
}

function looksLikeGeneratedContextualPersonIdentity(value, bodyFieldLabels = []) {
  const text = String(value || '').trim().replace(/\.$/, '');
  const words = text.split(/\s+/).filter(Boolean);
  if (
    words.length < 1
    || words.length > 6
    || GENERATED_BUSINESS_SIGNAL_PATTERN.test(text)
    || GENERATED_PROSE_SIGNAL_PATTERN.test(text)
    || looksLikeGeneratedBodyFieldLabel(text, bodyFieldLabels)
    || looksLikeGeneratedBodyHeading(text)
    || looksLikeGeneratedRoleDetail(text)
    || looksLikeGeneratedProfessionalDetail(text)
  ) return false;
  return words.every(word => {
    const cleaned = word.replace(/^[^\p{L}]+|[^\p{L}\p{M}.'’–-]+$/gu, '');
    return Boolean(cleaned && /^\p{L}/u.test(cleaned));
  });
}

function looksLikeGeneratedJoinedOrganization(value, bodyFieldLabels = []) {
  const detail = getBoundedGeneratedDetailWords(value, { allowLegalSuffix: true });
  if (!detail || !GENERATED_JOINED_ORGANIZATION_PATTERN.test(detail.text)) return false;
  const joinedParts = detail.text.split(/\s+(?:&|and)\s+/i);
  return !looksLikeGeneratedBodyHeading(detail.text)
    && !looksLikeGeneratedBodyFieldLabel(detail.text, bodyFieldLabels)
    && !looksLikeGeneratedRoleDetail(detail.text)
    && !looksLikeGeneratedProfessionalDetail(detail.text)
    && joinedParts.every(part => (
      !looksLikeGeneratedBodyHeading(part)
      && !looksLikeGeneratedBodyFieldLabel(part, bodyFieldLabels)
      && !looksLikeGeneratedRoleDetail(part)
      && !looksLikeGeneratedProfessionalDetail(part)
    ));
}

function looksLikeGeneratedOrganization(value, bodyFieldLabels = []) {
  const detail = getBoundedGeneratedDetailWords(value, { allowLegalSuffix: true });
  return Boolean(
    detail
    && (
      GENERATED_BUSINESS_SIGNAL_PATTERN.test(detail.text)
      || looksLikeGeneratedJoinedOrganization(detail.text, bodyFieldLabels)
    )
  );
}

function getBoundedGeneratedDetailWords(value, { allowLegalSuffix = false } = {}) {
  const rawText = String(value || '').trim();
  const text = allowLegalSuffix
    ? rawText.replace(GENERATED_LEGAL_SUFFIX_PATTERN, match => match.slice(0, -1))
    : rawText;
  const words = text.split(/\s+/).filter(Boolean);
  if (!text || text.length > 80 || words.length > 8 || GENERATED_PROSE_SIGNAL_PATTERN.test(text)) return null;
  return { text, words };
}

function looksLikeGeneratedRoleDetail(value) {
  const detail = getBoundedGeneratedDetailWords(value);
  if (!detail) return false;
  if (GENERATED_ACRONYM_ROLE_PATTERN.test(detail.text) || GENERATED_DEPARTMENT_PATTERN.test(detail.text)) {
    return true;
  }
  const cleanedWords = detail.words.map(word => (
    word.replace(/^[^\p{L}]+|[^\p{L}\p{M}.'’/-]+$/gu, '')
  ));
  const roleParts = cleanedWords.flatMap(word => word.split(/[/-]/).filter(Boolean));
  const terminalRole = roleParts[roleParts.length - 1] || '';
  if (GENERATED_FIELD_TERMINAL_PATTERN.test(terminalRole)) return false;
  return roleParts.some(part => (
    GENERATED_ACRONYM_ROLE_PATTERN.test(part) || GENERATED_ROLE_NOUN_PATTERN.test(part)
  )) || (roleParts.length === 1 && GENERATED_DEPARTMENT_PATTERN.test(terminalRole));
}

function looksLikeGeneratedBusinessDetail(value, bodyFieldLabels = []) {
  const detail = getBoundedGeneratedDetailWords(value, { allowLegalSuffix: true });
  return Boolean(
    detail
    && (
      GENERATED_BUSINESS_SIGNAL_PATTERN.test(detail.text)
      || looksLikeGeneratedJoinedOrganization(detail.text, bodyFieldLabels)
    )
  );
}

function looksLikeGeneratedAtomicContactDetail(value) {
  const text = String(value || '').trim();
  if (GENERATED_CONTACT_DETAIL_PATTERN.test(text)) return true;
  return /[.;]$/.test(text) && GENERATED_CONTACT_DETAIL_PATTERN.test(text.slice(0, -1));
}

function looksLikeGeneratedContactDetail(value) {
  const text = String(value || '').trim();
  if (looksLikeGeneratedAtomicContactDetail(text)) return true;
  return [
    /\s*(?:\||•|·)\s*/u,
    /\s+\/\s+/u,
    /\s*;\s*/u,
    /\s*,\s*/u,
    /\s{2,}/u
  ].some(separator => {
    const parts = text.split(separator).filter(Boolean);
    return parts.length > 1 && parts.every(looksLikeGeneratedAtomicContactDetail);
  });
}

function looksLikeGeneratedAtomicProfessionalDetail(value) {
  const text = String(value || '').trim();
  return GENERATED_LICENSE_DETAIL_PATTERN.test(text)
    || GENERATED_LOCATION_DETAIL_PATTERN.test(text)
    || GENERATED_ADDRESS_DETAIL_PATTERN.test(text)
    || looksLikeGeneratedCredentialDetail(text)
    || GENERATED_TEAM_DETAIL_PATTERN.test(text);
}

function looksLikeGeneratedCredentialDetail(value) {
  const text = String(value || '').trim();
  if (GENERATED_CREDENTIAL_DETAIL_PATTERN.test(text)) return true;
  const parts = text.split(/\s*(?:\||•|·)\s*|\s+\/\s+/u).filter(Boolean);
  return parts.length > 1 && parts.every(part => GENERATED_CREDENTIAL_DETAIL_PATTERN.test(part));
}

function looksLikeGeneratedProfessionalDetail(value) {
  const text = String(value || '').trim();
  if (looksLikeGeneratedAtomicProfessionalDetail(text)) return true;
  return [
    /\s*(?:\||•|·)\s*/u,
    /\s+\/\s+/u,
    /\s*;\s*/u,
    /\s*,\s*/u,
    /\s{2,}/u
  ].some(separator => {
    const parts = text.split(separator).filter(Boolean);
    return parts.length > 1 && parts.every(part => (
      looksLikeGeneratedAtomicContactDetail(part)
      || looksLikeGeneratedAtomicProfessionalDetail(part)
    ));
  });
}

function looksLikeGeneratedContextualOrganization(value, bodyFieldLabels = []) {
  const text = String(value || '').trim();
  if (
    !text
    || GENERATED_NO_ADDRESSEE_BODY_PATTERN.test(text)
    || GENERATED_PROSE_SIGNAL_PATTERN.test(text)
    || looksLikeGeneratedBodyContent(text, bodyFieldLabels)
    || looksLikeGeneratedRoleDetail(text)
    || looksLikeGeneratedProfessionalDetail(text)
    || looksLikeGeneratedContactDetail(text)
  ) return false;
  return /^\p{Ll}[\p{L}\p{M}'’.-]{1,30}$/u.test(text)
    || /^\p{Ll}[\p{L}\p{M}'’.-]*(?:\s+\p{Ll}[\p{L}\p{M}'’.-]*){0,3}\s+(?:&|and)\s+\p{Ll}[\p{L}\p{M}'’.-]*(?:\s+\p{Ll}[\p{L}\p{M}'’.-]*){0,3}$/u.test(text);
}

function looksLikeGeneratedBodyHeading(value) {
  const text = String(value || '').trim().replace(/:$/, '');
  return GENERATED_BODY_HEADING_PATTERN.test(text);
}

function looksLikeGeneratedIdentitySuffix(
  lines,
  bodyFieldLabels = [],
  { allowContactOnly = true, allowRoleOnly = false, requireSupportingEvidence = false } = {}
) {
  const contentLines = lines.map(line => line.trim()).filter(Boolean);
  if (contentLines.length === 0) return false;
  if (contentLines.some(line => GENERATED_SIGNATURE_MARKER_PATTERN.test(line))) return true;
  if (
    allowContactOnly
    && contentLines.every(looksLikeGeneratedContactDetail)
  ) return true;
  const firstLineIsRole = looksLikeGeneratedRoleDetail(contentLines[0]);
  if (
    looksLikeGeneratedBodyHeading(contentLines[0])
    || (
      looksLikeGeneratedBodyFieldLabel(contentLines[0], bodyFieldLabels)
      && (
        looksLikeConfiguredGeneratedBodyFieldLabel(contentLines[0], bodyFieldLabels)
        || !(allowRoleOnly && firstLineIsRole)
      )
    )
  ) return false;
  const hasRoleOrProfessionalDetail = contentLines.some(line => (
    looksLikeGeneratedRoleDetail(line) || looksLikeGeneratedProfessionalDetail(line)
  ));
  const hasSupportingIdentityEvidence = contentLines.some(line => (
    looksLikeGeneratedContactDetail(line)
    || looksLikeGeneratedRoleDetail(line)
    || looksLikeGeneratedBusinessDetail(line, bodyFieldLabels)
    || looksLikeGeneratedProfessionalDetail(line)
  ));
  const hasTrailingContextualOrganization = contentLines.slice(1).some(line => (
    looksLikeGeneratedContextualOrganization(line, bodyFieldLabels)
  ));
  if (
    allowRoleOnly
    && hasRoleOrProfessionalDetail
    && contentLines.every(line => (
      looksLikeGeneratedContactDetail(line)
      || looksLikeGeneratedRoleDetail(line)
      || looksLikeGeneratedBusinessDetail(line, bodyFieldLabels)
      || looksLikeGeneratedProfessionalDetail(line)
    ))
  ) return true;

  const firstContentLine = contentLines[0];
  const trailingContentLines = contentLines.slice(1);
  const hasTrailingRoleEvidence = trailingContentLines.some(line => (
    looksLikeGeneratedRoleDetail(line)
  ));
  const roleParts = firstContentLine.split(/\s*(?:\||[–—])\s*|\s+[/-]\s+/u);
  if (
    roleParts.length > 1
    && looksLikeGeneratedPersonIdentity(roleParts[0], bodyFieldLabels)
    && roleParts.slice(1).every(part => (
      looksLikeGeneratedRoleDetail(part)
      || looksLikeGeneratedBusinessDetail(part, bodyFieldLabels)
      || looksLikeGeneratedProfessionalDetail(part)
      || looksLikeGeneratedContactDetail(part)
    ))
  ) return true;
  const hasIdentityFirstLine = (
    looksLikeGeneratedPersonIdentity(firstContentLine, bodyFieldLabels)
      || looksLikeGeneratedOrganization(firstContentLine, bodyFieldLabels)
      || looksLikeGeneratedContextualOrganization(firstContentLine, bodyFieldLabels)
      || (
        hasTrailingRoleEvidence
        && looksLikeGeneratedContextualPersonIdentity(firstContentLine, bodyFieldLabels)
      )
  );
  if (!hasIdentityFirstLine) return false;
  if (
    requireSupportingEvidence
    && !hasSupportingIdentityEvidence
    && !hasTrailingContextualOrganization
  ) return false;
  return contentLines.length === 1 || contentLines.slice(1).every(line => (
    looksLikeGeneratedContactDetail(line)
      || looksLikeGeneratedPersonIdentity(line, bodyFieldLabels)
      || looksLikeGeneratedRoleDetail(line)
      || looksLikeGeneratedBusinessDetail(line, bodyFieldLabels)
      || looksLikeGeneratedProfessionalDetail(line)
      || looksLikeGeneratedContextualOrganization(line, bodyFieldLabels)
  ));
}

function allowsGeneratedRoleOnlySuffix(lines, valediction, bodyFieldLabels = []) {
  if (!GENERATED_BODY_LIKE_VALEDICTION_PATTERN.test(valediction)) return true;
  const contentLines = lines.map(line => line.trim()).filter(Boolean);
  return contentLines.length > 0
    && !contentLines.some(line => looksLikeGeneratedBodyFieldLabel(line, bodyFieldLabels))
    && contentLines.some(line => (
      looksLikeGeneratedRoleDetail(line) || looksLikeGeneratedCredentialDetail(line)
    ))
    && contentLines.every(line => (
      looksLikeGeneratedRoleDetail(line)
      || looksLikeGeneratedCredentialDetail(line)
      || looksLikeGeneratedContactDetail(line)
    ));
}

function splitGeneratedInlineValediction(line, trailingLines = [], bodyFieldLabels = []) {
  const text = String(line || '').trim();
  const separators = [
    /^(.+?),\s+(.+)$/u,
    /^(.+?)\s*[|–—]\s*(.+)$/u,
    /^(.+?)\s+-\s+(.+)$/u
  ];
  for (const separator of separators) {
    const match = text.match(separator);
    if (
      match
      && GENERATED_VALEDICTION_PATTERN.test(match[1].trim())
      && looksLikeGeneratedIdentitySuffix(
        [match[2], ...trailingLines],
        bodyFieldLabels,
        {
          allowRoleOnly: allowsGeneratedRoleOnlySuffix(
            [match[2], ...trailingLines],
            match[1].trim(),
            bodyFieldLabels
          )
        }
      )
    ) {
      return { valediction: match[1].trim(), identity: match[2].trim() };
    }
  }
  return null;
}

function stripGeneratedTerminalIdentity(script, bodyFieldLabels = []) {
  if (typeof script !== 'string') return script;
  if (!script.trim()) return '';
  const lines = script.replace(/\r\n?/g, '\n').trim().split('\n');

  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const trailingLines = lines.slice(index + 1);
    if (splitGeneratedInlineValediction(lines[index], trailingLines, bodyFieldLabels)) {
      return lines.slice(0, index).join('\n').trim();
    }
    const valediction = lines[index].trim();
    if (!GENERATED_VALEDICTION_PATTERN.test(valediction)) continue;
    const hasTrailingContent = trailingLines.some(line => line.trim());
    if (
      !hasTrailingContent
      || looksLikeGeneratedIdentitySuffix(
        trailingLines,
        bodyFieldLabels,
        {
          allowRoleOnly: allowsGeneratedRoleOnlySuffix(
            trailingLines,
            valediction,
            bodyFieldLabels
          )
        }
      )
    ) {
      return lines.slice(0, index).join('\n').trim();
    }
  }

  const markerIndex = lines.findIndex(line => GENERATED_SIGNATURE_MARKER_PATTERN.test(line));
  if (markerIndex >= 0) {
    let blockStart = markerIndex;
    while (blockStart > 0 && lines[blockStart - 1].trim()) blockStart -= 1;
    return lines.slice(0, blockStart).join('\n').trim();
  }

  let terminalBlockStart = lines.length;
  while (terminalBlockStart > 0 && lines[terminalBlockStart - 1].trim()) {
    terminalBlockStart -= 1;
  }
  let precedingContentIndex = terminalBlockStart - 1;
  while (precedingContentIndex >= 0 && !lines[precedingContentIndex].trim()) {
    precedingContentIndex -= 1;
  }
  const followsDeclinedValediction = precedingContentIndex >= 0
    && GENERATED_VALEDICTION_PATTERN.test(lines[precedingContentIndex].trim());
  if (
    terminalBlockStart > 0
    && !lines[terminalBlockStart - 1].trim()
    && !GENERATED_VALEDICTION_PATTERN.test(lines[terminalBlockStart].trim())
    && !followsDeclinedValediction
    && looksLikeGeneratedIdentitySuffix(
      lines.slice(terminalBlockStart),
      bodyFieldLabels,
      { allowRoleOnly: true, requireSupportingEvidence: true }
    )
  ) {
    return lines.slice(0, terminalBlockStart - 1).join('\n').trim();
  }

  return lines.join('\n').trim();
}

function hasGeneratedContiguousTerminalIdentity(script, bodyFieldLabels = []) {
  const lines = String(script || '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
  for (let index = Math.max(0, lines.length - 6); index < lines.length; index += 1) {
    const suffix = lines.slice(index);
    if (
      !GENERATED_BODY_TERMINAL_SIGNOFF_PATTERN.test(suffix[0])
      && looksLikeGeneratedPersonIdentity(suffix[0], bodyFieldLabels)
      && looksLikeGeneratedIdentitySuffix(
        suffix,
        bodyFieldLabels,
        { allowContactOnly: false, requireSupportingEvidence: true }
      )
    ) {
      return true;
    }
  }
  return false;
}

function hasGeneratedAttachedGreetingRecipient(value) {
  if (GENERATED_BODY_ATTACHED_GREETING_RECIPIENT_PATTERN.test(value)) return true;
  const prefix = String(value || '').match(GENERATED_BODY_ATTACHED_GREETING_PREFIX_PATTERN);
  return Boolean(prefix && GENERATED_BODY_MULTIWORD_ATTACHED_RECIPIENT_PATTERN.test(
    String(value).slice(prefix[0].length)
  ));
}

function containsGeneratedBodyPlaceholder(value) {
  return GENERATED_BODY_PLACEHOLDER_PATTERN.test(value)
    || GENERATED_BODY_INCOMPLETE_PLACEHOLDER_PATTERN.test(value)
    || GENERATED_BODY_UNICODE_PLACEHOLDER_PATTERN.test(value)
    || GENERATED_BODY_TOKEN_PLACEHOLDER_PATTERN.test(value)
    || GENERATED_BODY_REPEATED_PUNCTUATION_PLACEHOLDER_PATTERN.test(value);
}

function startsWithGeneratedBodyGreeting(value) {
  const text = String(value || '').trimStart();
  if (GENERATED_WEAK_GREETING_BODY_PROSE_PATTERN.test(text)) return false;
  return GENERATED_BODY_LEADING_GREETING_PATTERN.test(text)
    || hasGeneratedAttachedGreetingRecipient(text);
}

function hasGeneratedBodyGreetingAnywhere(value) {
  return String(value || '')
    .replace(/\r\n?/gu, '\n')
    .replace(/([.!?])\s+/gu, '$1\n')
    .split('\n')
    .map(segment => segment.trim())
    .filter(Boolean)
    .some(startsWithGeneratedBodyGreeting);
}

function readGeneratedIdentityBlock(lines, startIndex) {
  let index = startIndex;
  while (index < lines.length && !lines[index].trim()) index += 1;
  const block = [];
  while (index < lines.length && lines[index].trim() && block.length < 8) {
    block.push(lines[index].trim());
    index += 1;
  }
  return block;
}

function hasGeneratedIdentityAnywhere(value, bodyFieldLabels = []) {
  const text = String(value || '');
  if (GENERATED_SIGNATURE_MARKER_PATTERN.test(text)) return true;
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim();
    if (!line) continue;
    if (GENERATED_BODY_VALEDICTION_LINE_PATTERN.test(line)) {
      const identityBlock = readGeneratedIdentityBlock(lines, index + 1);
      if (
        identityBlock.length > 0
        && looksLikeGeneratedIdentitySuffix(
          identityBlock,
          bodyFieldLabels,
          {
            allowRoleOnly: allowsGeneratedRoleOnlySuffix(
              identityBlock,
              line,
              bodyFieldLabels
            )
          }
        )
      ) return true;
    }
    if (looksLikeGeneratedPersonIdentity(line, bodyFieldLabels)) {
      const identityBlock = readGeneratedIdentityBlock(lines, index);
      if (
        identityBlock.length > 1
        && looksLikeGeneratedIdentitySuffix(
          identityBlock,
          bodyFieldLabels,
          { allowContactOnly: false, requireSupportingEvidence: true }
        )
      ) return true;
    }
  }
  return false;
}

export function normalizeGeneratedCampaignScript(script, bodyFieldLabels = []) {
  const raw = typeof script === 'string' ? script : '';
  const boundary = parseLeadingGreetingBoundary(raw);
  if (boundary.kind === 'ambiguous') return raw;

  const body = boundary.kind === 'recognized'
    ? raw.slice(boundary.bodyStart)
    : raw;
  const withoutTerminalIdentity = stripGeneratedTerminalIdentity(body, bodyFieldLabels);
  if (!withoutTerminalIdentity) return withoutTerminalIdentity;
  return `${CAMPAIGN_GREETING_SKELETON}${withoutTerminalIdentity}`;
}

export function normalizeGeneratedCampaignScripts(scripts, bodyFieldLabels = []) {
  const rawScripts = Array.isArray(scripts) ? scripts : [];
  const ambiguousScriptIndexes = rawScripts
    .map((script, index) => (
      hasAmbiguousGeneratedWeakGreeting(script) ? index : null
    ))
    .filter(index => index !== null);
  const ambiguousIndexSet = new Set(ambiguousScriptIndexes);
  const normalizedScripts = rawScripts
    .map(script => normalizeGeneratedCampaignScript(script, bodyFieldLabels));
  return {
    ambiguousScriptIndexes,
    invalidScriptIndexes: normalizedScripts
      .map((script, index) => (
        !ambiguousIndexSet.has(index)
        && !hasCampaignMessageBody(script, { bodyFieldLabels })
          ? index
          : null
      ))
      .filter(index => index !== null),
    scripts: normalizedScripts
  };
}

export function normalizeServerGeneratedCampaignResponse(
  responseData,
  expectedCount,
  bodyFieldLabels = []
) {
  if (!responseData || typeof responseData !== 'object') {
    return { ok: false, reason: 'missing_contract', scripts: [] };
  }
  if (!Object.prototype.hasOwnProperty.call(responseData, 'generatedMessageContract')) {
    return { ok: false, reason: 'missing_contract', scripts: [] };
  }
  if (responseData.generatedMessageContract !== GENERATED_MESSAGE_CONTRACT) {
    return { ok: false, reason: 'invalid_contract', scripts: [] };
  }
  if (!Array.isArray(responseData.generatedBodies)) {
    return { ok: false, reason: 'invalid_bodies', scripts: [] };
  }
  if (
    !Number.isInteger(expectedCount)
    || expectedCount < 1
    || responseData.generatedBodies.length !== expectedCount
  ) {
    return { ok: false, reason: 'body_count_mismatch', scripts: [] };
  }

  const scripts = [];
  for (const value of responseData.generatedBodies) {
    if (typeof value !== 'string' || !value.trim()) {
      return { ok: false, reason: 'invalid_body', scripts: [] };
    }
    const body = value.trim();
    if (
      hasGeneratedBodyGreetingAnywhere(body)
      || containsGeneratedBodyPlaceholder(body)
      || GENERATED_BODY_TERMINAL_SIGNOFF_PATTERN.test(body)
      || hasGeneratedContiguousTerminalIdentity(body, bodyFieldLabels)
      || hasGeneratedIdentityAnywhere(body, bodyFieldLabels)
    ) {
      return { ok: false, reason: 'invalid_body', scripts: [] };
    }
    const withoutTerminalIdentity = stripGeneratedTerminalIdentity(body, bodyFieldLabels);
    if (!withoutTerminalIdentity || withoutTerminalIdentity !== body) {
      return { ok: false, reason: 'invalid_body', scripts: [] };
    }
    scripts.push(`${CAMPAIGN_GREETING_SKELETON}${body}`);
  }

  return { ok: true, reason: null, scripts };
}

// Build ONE combined outreach body covering all of a broker's properties.
// Reuses the primary script's ask/voice but swaps the single-property greeting
// for one that bullet-lists every property. Keeps the [NAME] placeholder so the
// normal personalization path still fills in the contact's first name.
export function buildCombinedScript(primaryScript, properties) {
  // The composer keeps the advisor's own greeting word, so the primary may open
  // with Hello, Dear or Good morning. Strip it with the shared greeting
  // vocabulary and reuse the same word here, or the combined message carries a
  // second greeting the product inserted and the launch refuses it.
  const raw = String(primaryScript || '');
  const boundary = parseLeadingGreetingBoundary(raw);
  const head = matchLeadingGreetingHead(raw);
  const greeting = boundary.kind === 'recognized'
    ? boundary.greeting
    : (head?.greeting || 'Hi');
  let body;
  if (boundary.kind === 'recognized') {
    body = raw.slice(boundary.bodyStart).trim();
  } else if (head) {
    const greetingLineEnd = raw.indexOf('\n', head.restStart);
    body = greetingLineEnd < 0 ? raw.trim() : raw.slice(greetingLineEnd + 1).trim();
  } else {
    body = raw.trim();
  }
  // The suite is what tells two spaces at one street address apart. Without it
  // a broker holding Suite 340 and Suite 415 reads the same bullet twice.
  const bullets = (properties || [])
    .map(p => {
      const label = buildCampaignPropertySubject(p);
      return label ? `• ${label}` : null;
    })
    .filter(Boolean)
    .join('\n');
  return `${greeting} [NAME],\n\nI'm interested in several of your listings:\n\n${bullets}\n\n${body}`;
}

function buildPersonalizedCampaignEmails({
  askFields,
  brokerData,
  normalizedScripts,
  recipientPlanByKey,
  recipientVariableReviewByKey,
  sendMode,
}) {
  const personalizedEmails = [];
  const combineMode = sendMode === 'combined';

  Object.entries(brokerData).forEach(([email, properties]) => {
    const combineThisBroker = combineMode && properties.length > 1;
    let combinedScript = null;
    let combinedSubject = null;
    if (combineThisBroker) {
      combinedScript = normalizeScriptWithPlaceholder(
        buildCombinedScript(normalizedScripts[0], properties)
      );
      const firstProp = properties[0] || {};
      const firstSubject = buildCampaignPropertySubject(firstProp) || '';
      combinedSubject = `${firstSubject} (+${properties.length - 1} more)`;
    }

    properties.forEach((property, propIndex) => {
      const plannedRecipient = recipientPlanByKey[`${email}-${propIndex}`];
      const recipientReview = recipientVariableReviewByKey[`${email}-${propIndex}`];
      const scriptIndex = plannedRecipient?.scriptIndex || 0;
      const baseScript = combineThisBroker ? combinedScript : normalizedScripts[scriptIndex];
      const resolvedContactName = plannedRecipient?.resolvedContactName || null;
      const injectionFirstName = recipientReview?.injectionFirstName || '';

      personalizedEmails.push({
        email,
        script: injectNameIntoScript(baseScript, injectionFirstName, { bodyFieldLabels: askFields }),
        scriptIndex: combineThisBroker ? 0 : scriptIndex,
        contactName: resolvedContactName,
        firstName: injectionFirstName,
        property: {
          address: property.address,
          city: property.city,
          propertyName: property.propertyName,
          rowIndex: property.rowIndex
        },
        ...(combineThisBroker ? { sendMode: 'combined', combinedSubject } : {})
      });
    });
  });

  return personalizedEmails;
}

export function buildCampaignLaunchPreflight({
  askFields,
  brokerData,
  campaignLabel,
  campaignProperties,
  hasAskContract,
  neededScriptCount,
  nonRequestableFields = [],
  recipientPlanByKey,
  recipientVariableReviews,
  recipientVariableReviewByKey,
  scripts,
  secondaryGenerationReason = '',
  secondaryGenerationStatus = 'idle',
  sendMode,
}) {
  const normalizedScripts = normalizeCampaignScriptsForLaunch(scripts, { bodyFieldLabels: askFields });
  const invalidRequiredScriptIndexes = findInvalidRequiredCampaignScriptIndexes(
    normalizedScripts,
    { sendMode, neededScriptCount, bodyFieldLabels: askFields }
  );
  const invalidRequiredScriptIndexSet = new Set(invalidRequiredScriptIndexes);
  const blockerByKey = new Map();
  const addBlocker = (key, message) => {
    if (!blockerByKey.has(key)) blockerByKey.set(key, { key, message });
  };

  // The per-property messages are the product's job, not the advisor's: while
  // the generator is running or after it has failed, the launch is blocked by
  // THAT reason rather than by a list of empty messages the advisor never wrote.
  const missingSecondaryScriptIndexes = invalidRequiredScriptIndexes.filter(index => index > 0);
  invalidRequiredScriptIndexes
    .filter(index => index === 0 || secondaryGenerationStatus === 'idle')
    .forEach(scriptIndex => {
      addBlocker(
        `body:${scriptIndex}`,
        `Message ${scriptIndex + 1} needs email body text; placeholders and punctuation alone cannot be sent.`
      );
    });

  if (missingSecondaryScriptIndexes.length > 0 && secondaryGenerationStatus === 'generating') {
    addBlocker('secondaryGeneration:generating', SECONDARY_GENERATION_RUNNING_MESSAGE);
  }
  if (missingSecondaryScriptIndexes.length > 0 && secondaryGenerationStatus === 'failed') {
    // The server's reason is the only actionable half of this blocker: without
    // it the advisor retries advice that will fail identically every time.
    const reason = String(secondaryGenerationReason || '').trim();
    addBlocker(
      'secondaryGeneration:failed',
      reason
        ? `${SECONDARY_GENERATION_FAILED_MESSAGE} ${reason}`
        : SECONDARY_GENERATION_FAILED_MESSAGE
    );
  }

  if (Object.keys(brokerData).length === 0) {
    addBlocker('recipient:none', 'Add at least one recipient before launch.');
  }

  // A column configured as Note, Skip or formula is a fact about the campaign,
  // not about a recipient, so it is read off every script before the recipient
  // fan-out exists - the operator only ever has one message in view, and the
  // conflict is just as likely to be in the one they are not looking at. This
  // runs whether or not an Ask contract was passed through: an unknown Ask set
  // never licenses asking for a column the advisor marked never-ask.
  normalizedScripts.forEach((script, scriptIndex) => {
    if (invalidRequiredScriptIndexSet.has(scriptIndex)) return;
    describeNonRequestableRequests(script, nonRequestableFields).forEach(guidance => {
      addBlocker(
        `nonRequestable:${scriptIndex}:${guidance}`,
        `Message ${scriptIndex + 1}: ${guidance}`
      );
    });
  });

  recipientVariableReviews.filter(review => review.isBlocking).forEach(review => {
    const propertyLabel = review.property?.address || review.property?.propertyName || 'Unknown property';
    if (review.requiresContactResolution) {
      addBlocker(
        `identity:${review.id}:contact`,
        `Set the full Leasing Contact for ${propertyLabel} (${review.email}) before launch. This shared mailbox has multiple known contacts, and a greeting override cannot resolve the row's recipient identity.`
      );
    } else if (review.requiresPersonalization) {
      addBlocker(
        `identity:${review.id}:personalization`,
        `Use the [NAME] placeholder in the campaign message before launch. This message opens with a fixed greeting, so every recipient would receive the same literal name instead of their own.`
      );
    } else {
      addBlocker(
        `identity:${review.id}:name`,
        `Add the first name for ${propertyLabel} (${review.email}) before launch. This campaign message uses [NAME], so SiteSift needs a confirmed recipient name.`
      );
    }
  });

  const personalizedEmails = buildPersonalizedCampaignEmails({
    askFields,
    brokerData,
    normalizedScripts,
    recipientPlanByKey,
    recipientVariableReviewByKey,
    sendMode,
  });

  personalizedEmails.forEach(emailData => {
    if (invalidRequiredScriptIndexSet.has(emailData.scriptIndex)) return;

    const validation = validateCampaignLaunchDraft(emailData.script, {
      ...(hasAskContract ? { askFields } : {}),
      forbiddenValues: buildCampaignDraftForbiddenValues({
        campaignLabel,
        campaignProperties,
        currentProperty: emailData.property,
        sendMode: emailData.sendMode || 'separate',
        brokerProperties: brokerData[emailData.email] || [],
      }),
    });
    emailData.launchValidation = validation;

    validation.errors.forEach(problem => {
      const matchedValue = /internal campaign label|another property/i.test(problem)
        ? validation.scopeMatches?.[0]?.value || null
        : null;
      const propertyLabel = emailData.property?.address
        || emailData.property?.propertyName
        || 'Unknown property';
      const correction = matchedValue ? ` Remove “${matchedValue}”.` : '';
      const isRecipientSpecific = /email\/local-part greeting/i.test(problem);
      const rowIdentity = `${emailData.email}:${emailData.property?.rowIndex ?? ''}:${propertyLabel}`;
      const blockerScope = matchedValue || isRecipientSpecific
        ? `row:${rowIdentity}`
        : `script:${emailData.scriptIndex}`;
      addBlocker(
        `draft:${blockerScope}:${problem}:${matchedValue || ''}`,
        `Message ${emailData.scriptIndex + 1} for ${propertyLabel} (${emailData.email}): ${problem}${correction}`
      );
    });
  });

  return {
    blockers: [...blockerByKey.values()],
    normalizedScripts,
    personalizedEmails,
  };
}

export default function StartProjectModal({ client, onClose, onConfirm, embedded = false, mailboxConnected = true }) {
  const navigate = embedded ? null : useNavigate(); // eslint-disable-line react-hooks/rules-of-hooks
  const modalRef = useRef();
  const modalContainerRef = useRef();
  const launchPendingRef = useRef(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const { sessionData, refreshAllData } = useSessionStorage();
  const isLoadingProfile = sessionData.isLoadingProfile;

  // Build initial transport groups from emailToProperties or fall back to assignedEmails.
  // Contact fallback is resolved later by the recipient planner so conflicting
  // contacts sharing one mailbox cannot leak onto a blank row.
  const getInitialBrokerData = () => {
    let brokerData = {};

    if (client.emailToProperties && Object.keys(client.emailToProperties).length > 0) {
      brokerData = JSON.parse(JSON.stringify(client.emailToProperties));
    } else {
      (client.assignedEmails || []).forEach((email, index) => {
        if (!brokerData[email]) {
          brokerData[email] = [];
        }
        brokerData[email].push({
          rowIndex: index + 3,
          address: `Property ${brokerData[email].length + 1}`,
          city: '',
          contact: client.emailToContactMap?.[email] || ''
        });
      });
    }

    return brokerData;
  };

  const [brokerData, setBrokerData] = useState(getInitialBrokerData);
  const recipientPlan = useMemo(() => buildContactAwareRecipientPlan({
    brokerData,
    emailToContactMap: client.emailToContactMap || {}
  }), [brokerData, client.emailToContactMap]);
  const recipientPlanByKey = useMemo(() => recipientPlan.reduce((acc, recipient) => {
    acc[recipient.id] = recipient;
    return acc;
  }, {}), [recipientPlan]);
  const neededScriptCount = Math.max(
    1,
    ...recipientPlan.map(recipient => recipient.contactOrdinal + 1)
  );
  const campaignScopeProperties = Object.values(brokerData)
    .flatMap(properties => Array.isArray(properties) ? properties : [])
    .map(property => ({
      address: property?.address || '',
      propertyName: property?.propertyName || '',
    }));

  // Derived values for display
  const uniqueContactCount = new Set(
    recipientPlan.map(recipient => recipient.identityKey)
  ).size;
  const recipientContactLabelByEmail = useMemo(() => {
    const contactNamesByEmail = new Map();
    const multipleKnownContactsByEmail = new Set();
    recipientPlan.forEach(recipient => {
      if (recipient.hasMultipleKnownContacts) {
        multipleKnownContactsByEmail.add(recipient.email);
      }
      const contactName = String(recipient.resolvedContactName || '').trim();
      if (!contactName) return;
      if (!contactNamesByEmail.has(recipient.email)) {
        contactNamesByEmail.set(recipient.email, new Map());
      }
      const normalizedContactName = contactName.replace(/\s+/g, ' ').toLowerCase();
      const contactNames = contactNamesByEmail.get(recipient.email);
      if (!contactNames.has(normalizedContactName)) {
        contactNames.set(normalizedContactName, contactName);
      }
    });

    const labels = new Map();
    Object.keys(brokerData).forEach(email => {
      const contactNames = contactNamesByEmail.get(email) || new Map();
      labels.set(
        email,
        multipleKnownContactsByEmail.has(email) || contactNames.size > 1
          ? 'Multiple contacts'
          : contactNames.values().next().value || 'Unknown contact'
      );
    });
    return labels;
  }, [brokerData, recipientPlan]);
  const totalPropertyCount = Object.values(brokerData).reduce((sum, props) => sum + props.length, 0);

  const askFields = useMemo(
    () => (Array.isArray(client?.askFields) && client.askFields.length > 0
      ? client.askFields
      : deriveAskFields(client?.columnConfig)),
    [client?.askFields, client?.columnConfig]
  );
  const hasAskContract = Boolean(client?.columnConfig) || Array.isArray(client?.askFields);
  // The other half of the column contract: what SiteSift must never request.
  const nonRequestableFields = useMemo(
    () => (Array.isArray(client?.nonRequestableFields)
      ? client.nonRequestableFields
      : deriveNonRequestableFields(client?.columnConfig)),
    [client?.nonRequestableFields, client?.columnConfig]
  );

  // Email Scripts - array of all scripts (primary + follow-ups)
  const getUploadedScripts = () => selectUploadedCampaignScripts({
    emailScripts: client.emailScripts,
    criteria: client.criteria,
    secondaryCriteria: client.secondaryCriteria,
  });

  const uploadedScripts = useRef(getUploadedScripts());
  // The workbook's own contacts decide whether a literal name in an uploaded
  // greeting is a recipient (replace it with [NAME]) or the advisor's opening
  // phrase (leave every word of it alone).
  const uploadedContactNames = useRef([
    ...Object.values(client?.emailToContactMap || {}),
    ...Object.values(client?.emailToProperties || {})
      .flatMap(properties => (Array.isArray(properties) ? properties : []))
      .map(property => property?.contact),
  ].filter(name => typeof name === 'string' && name.trim()));
  const initialScripts = useRef(prepareUploadedCampaignScripts(
    uploadedScripts.current,
    { bodyFieldLabels: askFields, contactNames: uploadedContactNames.current }
  ));
  // Greeting-only uploads still need body copy before they are prepared.
  const hadInitialScripts = useRef(hasCampaignMessageBody(
    initialScripts.current[0],
    { bodyFieldLabels: askFields }
  ));

  const [scripts, setScripts] = useState(initialScripts.current);
  // Store original uploaded scripts for reset functionality
  const originalScripts = useRef(
    uploadedScripts.current.length > 0 ? initialScripts.current : []
  );
  const scriptRefs = useRef([]);
  const followUpRefs = useRef([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingIndex, setGeneratingIndex] = useState(null);
  const [isBatchGenerating, setIsBatchGenerating] = useState(false);
  // 'idle' | 'generating' | 'failed' - why the per-property messages are missing.
  const [secondaryGenerationStatus, setSecondaryGenerationStatus] = useState('idle');
  // The callable enforces the never-ask contract, so its failure carries the one
  // sentence that says how to fix the message. Keep it and show it.
  const [secondaryGenerationReason, setSecondaryGenerationReason] = useState('');
  const [hasGeneratedGreetingBoundaryError, setHasGeneratedGreetingBoundaryError] = useState(false);
  const hasAutoGenerated = useRef(false);
  const [variableReviewError, setVariableReviewError] = useState(false);

  const acceptGeneratedScripts = (responseData, expectedCount) => {
    const result = normalizeServerGeneratedCampaignResponse(
      responseData,
      expectedCount,
      askFields
    );
    if (!result.ok) {
      setHasGeneratedGreetingBoundaryError(true);
      return null;
    }
    setHasGeneratedGreetingBoundaryError(false);
    return result.scripts;
  };

  // Track if campaign is ready to send (scripts prepared)
  const [campaignPrepared, setCampaignPrepared] = useState(hadInitialScripts.current);

  // Send mode for brokers that appear on 2+ property rows:
  // 'separate' (default) = one email per property; 'combined' = one email listing
  // all of that broker's properties. Only surfaced when a multi-property broker exists.
  const [sendMode, setSendMode] = useState('separate');

  // Switching send mode after preparing invalidates the prepared campaign: wipe the
  // generated scripts and drop back to "not prepared" so the user re-prepares and the
  // scripts get remade for the new mode. Lets them change their mind either way.
  const handleSendModeChange = (mode) => {
    if (mode === sendMode) return;
    setSendMode(mode);
    setCampaignPrepared(false);
    setScripts(prev => (prev.length > 1 ? [prev[0]] : prev));
    setHasGeneratedGreetingBoundaryError(false);
    hasAutoGenerated.current = false;
  };

  // Get signature from context (no need for separate fetch)
  const signatureMode = sessionData.signatureMode || sessionData.userProfile?.signatureMode;
  const emailSignature = useMemo(() => buildActiveSignatureFromProfile({
    ...(sessionData.userProfile || {}),
    emailSignature: sessionData.emailSignature ?? sessionData.userProfile?.emailSignature,
    signatureMode
  }, sessionData.organizationName), [sessionData.userProfile, sessionData.emailSignature, signatureMode, sessionData.organizationName]);

  // Follow-up settings state
  const [followUpCount, setFollowUpCount] = useState(0); // 0, 1, 2, or 3
  const [followUps, setFollowUps] = useState([
    { waitTime: 5, waitUnit: 'days', message: '' },
    { waitTime: 3, waitUnit: 'days', message: '' },
    { waitTime: 2, waitUnit: 'days', message: '' },
  ]);

  const launchNormalizedScripts = useMemo(
    () => normalizeCampaignScriptsForLaunch(scripts, { bodyFieldLabels: askFields }),
    [askFields, scripts]
  );
  const recipientVariableReviews = useMemo(() => buildRecipientVariableReviews({
    recipientPlan,
    scripts: launchNormalizedScripts
  }), [recipientPlan, launchNormalizedScripts]);
  const recipientVariableReviewByKey = useMemo(() => (
    recipientVariableReviews.reduce((acc, review) => {
      acc[review.id] = review;
      return acc;
    }, {})
  ), [recipientVariableReviews]);
  const blockingRecipientVariableIssue = findBlockingRecipientVariableIssue(recipientVariableReviews);
  const recipientVariableIssueCount = recipientVariableReviews.filter(review => review.isBlocking).length;
  const recipientContactResolutionIssueCount = recipientVariableReviews
    .filter(review => review.requiresContactResolution).length;
  const recipientNameIssueCount = recipientVariableReviews.filter(review => review.requiresName).length;
  const recipientVariableReadyCount = recipientVariableReviews.length - recipientVariableIssueCount;

  // Update follow-up message
  const updateFollowUp = (index, field, value) => {
    setFollowUps(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Reset follow-up to default. R1 follow-up copy, 25 Sep: an empty message
  // shows the default in the box and is saved empty, so the backend chooses.
  const resetFollowUpMessage = (index) => {
    updateFollowUp(index, 'message', '');
  };

  // Refresh profile data on mount if signature not loaded
  // This handles cases where cache expired or wasn't populated
  useEffect(() => {
    if (signatureMode === null && !isLoadingProfile) {
      refreshAllData();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Settings navigation handler - suppressed in embedded mode
  const goToSettings = embedded ? null : () => { onClose(); navigate('/settings'); };

  // Extract primary script for dependency tracking
  const primaryScript = scripts[0];

  // Campaign context for drafting an INITIAL primary from a blank box.
  // Prefer explicit context threaded from AddClientModal; fall back to whatever
  // the client object carries (real-client / standalone usage of this modal).
  const campaignProperties = useMemo(
    () => deriveCampaignProperties({
      properties: client?.campaignProperties,
      emailToProperties: client?.emailToProperties,
    }),
    [client?.campaignProperties, client?.emailToProperties]
  );
  const explicitClientCriteria = useMemo(
    () => (typeof client?.clientCriteria === 'string' ? client.clientCriteria.trim() : ''),
    [client?.clientCriteria]
  );
  // The generator callables validate the primary against the canonical
  // "greeting, [NAME], blank line" wrapper. An uploaded one-line greeting is
  // refused there (primary_generated_body_contains_greeting), so send the same
  // normalized primary the launch gate reads rather than the raw editor text.
  const canonicalPrimaryScript = launchNormalizedScripts[0] || scripts[0] || '';
  const launchPreflightInput = useMemo(() => ({
    askFields,
    brokerData,
    campaignLabel: client?.name,
    campaignProperties,
    hasAskContract,
    neededScriptCount,
    nonRequestableFields,
    recipientPlanByKey,
    recipientVariableReviews,
    recipientVariableReviewByKey,
    scripts,
    secondaryGenerationReason,
    secondaryGenerationStatus,
    sendMode,
  }), [
    askFields,
    brokerData,
    campaignProperties,
    client?.name,
    hasAskContract,
    neededScriptCount,
    nonRequestableFields,
    recipientPlanByKey,
    recipientVariableReviews,
    recipientVariableReviewByKey,
    scripts,
    secondaryGenerationReason,
    secondaryGenerationStatus,
    sendMode,
  ]);
  const [resolvedLaunchPreflight, setResolvedLaunchPreflight] = useState(() => ({
    input: launchPreflightInput,
    result: buildCampaignLaunchPreflight(launchPreflightInput),
  }));
  const launchPreflightPending = resolvedLaunchPreflight.input !== launchPreflightInput;
  useEffect(() => {
    if (!launchPreflightPending) return undefined;

    const timeoutId = window.setTimeout(() => {
      setResolvedLaunchPreflight({
        input: launchPreflightInput,
        result: buildCampaignLaunchPreflight(launchPreflightInput),
      });
    }, LAUNCH_PREFLIGHT_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [launchPreflightInput, launchPreflightPending]);
  const preflightBlockers = launchPreflightPending
    ? [LAUNCH_PREFLIGHT_PENDING_BLOCKER]
    : resolvedLaunchPreflight.result.blockers;
  const boundaryBlockers = hasGeneratedGreetingBoundaryError
    ? [...preflightBlockers, GENERATED_GREETING_BOUNDARY_BLOCKER]
    : preflightBlockers;
  const mailboxNotConnected = mailboxConnected === false;
  const launchBlockers = mailboxNotConnected
    ? [...boundaryBlockers, MAILBOX_NOT_CONNECTED_BLOCKER]
    : boundaryBlockers;
  // Auto-generate missing scripts on mount (only if scripts existed initially)
  useEffect(() => {
    async function autoGenerateScripts() {
      // Only auto-generate if scripts existed when modal opened
      if (!hadInitialScripts.current) return;
      if (hasAutoGenerated.current) return;
      if (!primaryScript?.trim()) return;

      const currentCount = scripts.filter(s => s?.trim()).length;
      if (currentCount >= neededScriptCount) return;

      setIsBatchGenerating(true);
      setSecondaryGenerationStatus('generating');
      setSecondaryGenerationReason('');

      try {
        const functions = getFunctions();
        const generateAllFn = httpsCallable(functions, 'generateAllScripts');
        const result = await generateAllFn({
          primaryScript: canonicalPrimaryScript,
          count: neededScriptCount,
          askFields,
          nonRequestableFields,
        });

        const generatedScripts = acceptGeneratedScripts(
          result.data,
          neededScriptCount - 1
        );
        if (!generatedScripts) {
          setSecondaryGenerationStatus('failed');
          return;
        }
        setScripts(prev => {
          const updated = [prev[0]]; // Keep primary
          for (let i = 1; i < neededScriptCount; i++) {
            if (prev[i]?.trim()) {
              updated.push(prev[i]);
            } else if (generatedScripts[i - 1]) {
              updated.push(generatedScripts[i - 1]);
            } else {
              updated.push('');
            }
          }
          return updated;
        });
        hasAutoGenerated.current = true;
        setSecondaryGenerationStatus('idle');
      } catch (err) {
        console.error('Failed to auto-generate scripts:', err);
        setSecondaryGenerationStatus('failed');
        setSecondaryGenerationReason(describeGenerationFailure(err));
      } finally {
        setIsBatchGenerating(false);
      }
    }

    autoGenerateScripts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryScript, neededScriptCount]);

  // Manually prepare campaign (generate scripts when user clicks button)
  const prepareCampaign = async () => {
    if (!hasCampaignMessageBody(scripts[0], { bodyFieldLabels: askFields })) {
      alert('Please enter your primary email message first.');
      return;
    }

    setIsBatchGenerating(true);
    setSecondaryGenerationStatus('generating');
    setSecondaryGenerationReason('');

    try {
      // Separate mode needs one script variant per property. Combined mode sends a
      // single body per broker (assembled from the primary), so skip variant generation.
      if (sendMode !== 'combined' && neededScriptCount > 1) {
        const functions = getFunctions();
        const generateAllFn = httpsCallable(functions, 'generateAllScripts');
        const result = await generateAllFn({
          primaryScript: canonicalPrimaryScript,
          count: neededScriptCount,
          askFields,
          nonRequestableFields,
        });

        const generatedScripts = acceptGeneratedScripts(
          result.data,
          neededScriptCount - 1
        );
        if (!generatedScripts) {
          setSecondaryGenerationStatus('failed');
          return;
        }
        setScripts(prev => {
          const updated = [prev[0]]; // Keep primary
          for (let i = 1; i < neededScriptCount; i++) {
            if (prev[i]?.trim()) {
              updated.push(prev[i]);
            } else if (generatedScripts[i - 1]) {
              updated.push(generatedScripts[i - 1]);
            } else {
              updated.push('');
            }
          }
          return updated;
        });
      }

      // Mark campaign as prepared
      setCampaignPrepared(true);
      hasAutoGenerated.current = true;
      setSecondaryGenerationStatus('idle');

      // Scroll to top after a brief delay to let UI update
      setTimeout(() => {
        if (modalContainerRef.current) {
          modalContainerRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 300);
    } catch (err) {
      // The launch-issue list names the failure inline; a modal alert would
      // block the advisor on a message the product can already show in place.
      console.error('Failed to prepare campaign:', err);
      setSecondaryGenerationStatus('failed');
      setSecondaryGenerationReason(describeGenerationFailure(err));
    } finally {
      setIsBatchGenerating(false);
    }
  };

  // Regenerate all secondary scripts based on current primary
  const regenerateAllSecondaryScripts = async () => {
    if (!hasCampaignMessageBody(scripts[0], { bodyFieldLabels: askFields })) {
      alert('Please enter your primary email message first.');
      return;
    }

    if (neededScriptCount <= 1) return;

    setIsBatchGenerating(true);
    setSecondaryGenerationStatus('generating');
    setSecondaryGenerationReason('');

    try {
      const functions = getFunctions();
      const generateAllFn = httpsCallable(functions, 'generateAllScripts');
      const result = await generateAllFn({
        primaryScript: canonicalPrimaryScript,
        count: neededScriptCount,
        askFields,
        nonRequestableFields,
      });

      const generatedScripts = acceptGeneratedScripts(
        result.data,
        neededScriptCount - 1
      );
      if (!generatedScripts) {
        setSecondaryGenerationStatus('failed');
        return;
      }
      setScripts(prev => {
        const updated = [prev[0]]; // Keep primary unchanged
        for (let i = 1; i < neededScriptCount; i++) {
          updated.push(generatedScripts[i - 1] || '');
        }
        return updated;
      });
      setSecondaryGenerationStatus('idle');
    } catch (err) {
      console.error('Failed to regenerate scripts:', err);
      setSecondaryGenerationStatus('failed');
      setSecondaryGenerationReason(describeGenerationFailure(err));
    } finally {
      setIsBatchGenerating(false);
    }
  };

  // ESC to close (only for standalone modal)
  useEffect(() => {
    if (embedded) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !isAnimating) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isAnimating, onClose, embedded]);

  // Auto-fit textareas (primary scripts + follow-ups)
  const fitAllTextareas = () => {
    scriptRefs.current.forEach(el => {
      if (el) {
        el.style.height = 'auto';
        el.style.height = (el.scrollHeight + 10) + 'px';
      }
    });
    followUpRefs.current.forEach(el => {
      if (el) {
        el.style.height = 'auto';
        el.style.height = (el.scrollHeight + 10) + 'px';
      }
    });
  };

  useEffect(() => {
    fitAllTextareas();
    const onResize = () => fitAllTextareas();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    setTimeout(() => fitAllTextareas(), 0);
  }, [scripts]);

  useEffect(() => {
    if (!blockingRecipientVariableIssue) {
      setVariableReviewError(false);
    }
  }, [blockingRecipientVariableIssue]);

  // Re-fit follow-up textareas when they change
  useEffect(() => {
    setTimeout(() => fitAllTextareas(), 0);
  }, [followUps, followUpCount]);

  // Scroll indicator
  useEffect(() => {
    const modalContainer = modalContainerRef.current;
    if (!modalContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = modalContainer;
      const hasMoreContent = scrollHeight > clientHeight;
      const nearBottom = scrollTop + clientHeight >= scrollHeight - 100;
      setShowScrollIndicator(hasMoreContent && !nearBottom && scrollTop < 50);
    };

    modalContainer.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => modalContainer.removeEventListener('scroll', handleScroll);
  }, []);

  // Remove a specific property from a broker
  const removeProperty = (email, propertyIndex) => {
    const properties = brokerData[email] || [];
    const property = properties[propertyIndex];
    const propertyLabel = property?.address || `Property ${propertyIndex + 1}`;

    if (window.confirm(`Remove "${propertyLabel}" from ${email}?`)) {
      setBrokerData(prev => {
        const updated = { ...prev };
        const props = [...(updated[email] || [])];
        props.splice(propertyIndex, 1);
        if (props.length === 0) {
          delete updated[email];
        } else {
          updated[email] = props;
        }
        return updated;
      });
    }
  };

  // Remove entire broker with all their properties
  const removeBroker = (email) => {
    const properties = brokerData[email] || [];
    if (window.confirm(`Remove ${email} and all ${properties.length} property(s)?`)) {
      setBrokerData(prev => {
        const updated = { ...prev };
        delete updated[email];
        return updated;
      });
    }
  };

  // State for inline editing of first name (what [NAME] becomes)
  const [editingFirstName, setEditingFirstName] = useState(null); // { email, propIndex }
  const [editingFirstNameValue, setEditingFirstNameValue] = useState('');
  const firstNameSuggestionPendingRef = useRef(false);

  // Update first name override for a specific property
  const updatePropertyFirstName = (email, propIndex, newFirstName) => {
    setBrokerData(prev => {
      const updated = { ...prev };
      if (updated[email] && updated[email][propIndex]) {
        updated[email] = [...updated[email]];
        updated[email][propIndex] = {
          ...updated[email][propIndex],
          firstNameOverride: newFirstName.trim() || null
        };
      }
      return updated;
    });
  };

  // Start editing a first name
  const startEditingFirstName = (email, propIndex, currentValue, offerDerivedValue) => {
    // A workbook/contact-derived value is offered once. A saved manual override
    // is already the user's choice and must open with the caret at the end.
    firstNameSuggestionPendingRef.current = Boolean(offerDerivedValue && currentValue);
    setEditingFirstName({ email, propIndex });
    setEditingFirstNameValue(currentValue || '');
  };

  // Save edited first name
  const saveEditingFirstName = () => {
    if (editingFirstName) {
      updatePropertyFirstName(editingFirstName.email, editingFirstName.propIndex, editingFirstNameValue.trim());
      setEditingFirstName(null);
      setEditingFirstNameValue('');
    }
  };

  const handleScriptChange = (index, newValue) => {
    setHasGeneratedGreetingBoundaryError(false);
    const canonicalValue = normalizeNamePlaceholders(newValue);
    const hasPlaceholder = /\[NAME\]/i.test(canonicalValue);
    const greetingPattern = /^(Hi|Hello|Dear)\b/iu;

    let valueToSet = canonicalValue;

    if (greetingPattern.test(canonicalValue) && !hasPlaceholder) {
      valueToSet = normalizeScriptWithPlaceholder(canonicalValue, { bodyFieldLabels: askFields });

      setTimeout(() => {
        const textarea = scriptRefs.current[index];
        if (textarea) {
          const placeholderPos = getCampaignMessageBodyCaretOffset(valueToSet);
          setEditorCaret(textarea, placeholderPos);
        }
      }, 0);
    }

    setScripts(prev => {
      const updated = [...prev];
      updated[index] = valueToSet;
      return updated;
    });
  };

  // Reset a script to its original uploaded value
  const resetScriptToOriginal = (index) => {
    const original = originalScripts.current[index];
    if (original) {
      setHasGeneratedGreetingBoundaryError(false);
      setScripts(prev => {
        const updated = [...prev];
        updated[index] = original;
        return updated;
      });
    }
  };

  // Check if a script has been modified from original
  const isScriptModified = (index) => {
    const original = originalScripts.current[index];
    const current = scripts[index];
    return original && current !== original;
  };

  // Regenerate a script (primary or follow-up)
  const regenerateScript = async (targetIndex) => {
    // For primary script (index 0):
    //  - BLANK box  -> draft a fresh INITIAL outreach by reasoning over the
    //    campaign context (client, properties, "Ask" fields, any example scripts).
    //  - NON-BLANK  -> rewrite the existing draft (unchanged behavior).
    if (targetIndex === 0) {
      if (!hasCampaignMessageBody(scripts[0], { bodyFieldLabels: askFields })) {
        setIsGenerating(true);
        setGeneratingIndex(targetIndex);

        try {
          const functions = getFunctions();
          const generateInitialFn = httpsCallable(functions, 'generateInitialScript');

          const result = await generateInitialFn({
            campaignLabel: client?.name || '',
            clientCriteria: explicitClientCriteria,
            properties: campaignProperties,
            askFields,
            nonRequestableFields,
          });

          const [normalized] = acceptGeneratedScripts(result.data, 1) || [];
          if (normalized === undefined) return;
          setScripts(prev => {
            const updated = [...prev];
            updated[0] = normalized;
            return updated;
          });
        } catch (err) {
          console.error('Failed to generate initial primary script:', err);
          alert('Failed to generate a starting message. You can also type your primary message and use Regenerate.');
        } finally {
          setIsGenerating(false);
          setGeneratingIndex(null);
        }
        return;
      }

      setIsGenerating(true);
      setGeneratingIndex(targetIndex);

      try {
        const functions = getFunctions();
        const regeneratePrimaryFn = httpsCallable(functions, 'regeneratePrimaryScript');

        const result = await regeneratePrimaryFn({
          currentScript: canonicalPrimaryScript,
          clientName: client?.name || '',
          properties: campaignScopeProperties,
          askFields,
          nonRequestableFields,
        });

        if (result.data.preserved === false) {
          alert(['scope_conflict', 'property_semantics', 'rewrite_rejected'].includes(result.data.reason)
            ? 'Regenerate could not produce a safe rewrite for this campaign, so the message was left unchanged.'
            : 'Regenerate could not keep your stated requirements, so the message was left unchanged.');
          return;
        }

        const [normalized] = acceptGeneratedScripts(result.data, 1) || [];
        if (normalized === undefined) return;
        setScripts(prev => {
          const updated = [...prev];
          updated[0] = normalized;
          return updated;
        });
      } catch (err) {
        console.error('Failed to regenerate primary script:', err);
        alert('Failed to regenerate message. Please try again.');
      } finally {
        setIsGenerating(false);
        setGeneratingIndex(null);
      }
      return;
    }

    // For follow-up scripts (index > 0), need primary to exist
    if (!hasCampaignMessageBody(scripts[0], { bodyFieldLabels: askFields })) {
      alert('Please enter a primary message first.');
      return;
    }

    setIsGenerating(true);
    setGeneratingIndex(targetIndex);

    try {
      const functions = getFunctions();
      const generateFn = httpsCallable(functions, 'generateSecondaryScript');

      const otherScripts = scripts.filter((s, i) => i !== targetIndex && i !== 0 && s?.trim());

      const result = await generateFn({
        primaryScript: canonicalPrimaryScript,
        otherScripts,
        scriptIndex: targetIndex + 1,
        askFields,
        nonRequestableFields,
      });

      const [normalized] = acceptGeneratedScripts(result.data, 1) || [];
      if (normalized === undefined) return;
      setScripts(prev => {
        const updated = [...prev];
        while (updated.length <= targetIndex) {
          updated.push('');
        }
        updated[targetIndex] = normalized;
        return updated;
      });
    } catch (err) {
      console.error('Failed to regenerate script:', err);
      alert('Failed to regenerate message. Please try again.');
    } finally {
      setIsGenerating(false);
      setGeneratingIndex(null);
    }
  };

  const handleConfirm = async () => {
    if (launchPendingRef.current) return;
    const clickPreflight = buildCampaignLaunchPreflight({
      askFields,
      brokerData,
      campaignLabel: client?.name,
      campaignProperties,
      hasAskContract,
      neededScriptCount,
      nonRequestableFields,
      recipientPlanByKey,
      recipientVariableReviews,
      recipientVariableReviewByKey,
      scripts,
      secondaryGenerationReason,
      secondaryGenerationStatus,
      sendMode,
    });

    if (clickPreflight.blockers.length > 0) {
      setVariableReviewError(Boolean(blockingRecipientVariableIssue));
      requestAnimationFrame(() => {
        document.querySelector('[data-launch-draft-error="true"]')
          ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
      return;
    }

    const { normalizedScripts, personalizedEmails } = clickPreflight;

    launchPendingRef.current = true;
    let handoffSucceeded = false;
    try {
      const flatEmails = personalizedEmails.map(e => e.email);

      // Build follow-up config
      // R1 follow-up copy, 25 Sep: an untouched (or default) step is saved
      // with an empty message so the backend picks its silent or engaged copy.
      const followUpConfig = buildFollowUpConfig(followUps, followUpCount);

      // DEBUG: Log followUpConfig before passing to onConfirm
      console.log('🔧 [StartProjectModal] followUpCount:', followUpCount);
      console.log('🔧 [StartProjectModal] followUps state:', JSON.stringify(followUps, null, 2));
      console.log('🔧 [StartProjectModal] Built followUpConfig:', JSON.stringify(followUpConfig, null, 2));

      setIsAnimating(true);
      const handoffResult = await onConfirm({
        clientId: client.id,
        assignedEmails: flatEmails,
        emailToProperties: brokerData,
        script: normalizedScripts[0],
        secondaryScript: normalizedScripts[1] || null,
        emailScripts: normalizedScripts,
        personalizedEmails: personalizedEmails,
        followUpConfig: followUpConfig,
        sendMode: sendMode
      });
      if (handoffResult?.accepted === false) {
        setIsAnimating(false);
        return;
      }
      handoffSucceeded = true;

      console.log('🔧 [StartProjectModal] Called onConfirm with followUpConfig');
    } catch (error) {
      console.error('Failed to hand off campaign launch:', error);
      setIsAnimating(false);
      alert('SiteSift could not prepare this campaign. Nothing was queued. Please review the campaign and try again.');
    } finally {
      if (!handoffSucceeded) launchPendingRef.current = false;
    }
  };

  const getScriptLabel = (index) => {
    if (index === 0) return 'Primary Message';
    if (index === 1) return '2nd Contact';
    if (index === 2) return '3rd Contact';
    return `${index + 1}th Contact`;
  };

  const getOrdinalSuffix = (n) => {
    if (n === 1) return 'st';
    if (n === 2) return 'nd';
    if (n === 3) return 'rd';
    return 'th';
  };

  const protectPlaceholder = (e, index) => {
    // HighlightedTextarea (contenteditable) handles its own pill protection
    // This function is only needed for regular textareas
    const textarea = e.target;
    if (!textarea.value) return; // Skip for contenteditable

    const cursorPos = textarea.selectionStart;
    const placeholderIndex = textarea.value.indexOf('[NAME]');
    if (placeholderIndex === -1) return;
    const placeholderEnd = placeholderIndex + '[NAME]'.length;

    // Only block if cursor is truly INSIDE the placeholder (between [ and ])
    if (cursorPos > placeholderIndex && cursorPos < placeholderEnd) {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        setTimeout(() => setEditorCaret(textarea, placeholderEnd), 0);
      } else if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        if (e.key.length === 1 && !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
          e.preventDefault();
          setTimeout(() => setEditorCaret(textarea, placeholderEnd), 0);
        }
      }
    }
  };

  const modalContent = (
      <div ref={modalRef} className={`modal-backdrop-inner ${embedded ? 'modal-backdrop-inner--embedded' : ''}`} onMouseDown={(e) => !embedded && e.stopPropagation()}>
        <div ref={modalContainerRef} className={`modal-container ${embedded ? 'modal-container--embedded' : ''}`}>
          {/* Progress bar when sending */}
          {isAnimating && (
            <div className="sending-progress">
              <div className="sending-progress-bar" />
            </div>
          )}

          {/* Header */}
          <div className="modal-header">
            <div className="header-content">
              <div className="header-icon">
                <Building2 className="icon-building" />
              </div>
              <div>
                <h2 className="header-title">Outreach Campaign</h2>
                <p className="header-subtitle">{client.name}</p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="modal-body">
            <div className="step-container">
              <div className="step-content">
                {/* Primary Message Section */}
                <div className="script-section">
                  {(campaignPrepared || hasGeneratedGreetingBoundaryError || mailboxNotConnected) && launchBlockers.length > 0 && (
                    <div
                      id="campaign-launch-blockers"
                      className="launch-draft-error"
                      role="alert"
                      aria-live="polite"
                      data-launch-draft-error="true"
                    >
                      <AlertCircle size={18} />
                      <div>
                        <strong>
                          {launchPreflightPending
                            ? 'Checking launch safety before continuing'
                            : `Fix ${launchBlockers.length} launch issue${launchBlockers.length !== 1 ? 's' : ''} before continuing`}
                        </strong>
                        <ul>
                          {launchBlockers.map(blocker => (
                            <li key={blocker.key}>{blocker.message}</li>
                          ))}
                        </ul>
                        {mailboxNotConnected && <ConnectMailboxButton />}
                      </div>
                    </div>
                  )}
                  <div className="script-header">
                    <div className="script-header-info">
                      <Send className="script-header-icon" />
                      <div>
                        <h3 className="script-title">Primary Message</h3>
                        <p className="script-description">
                          This is the first email sent to each contact. The <strong>[NAME]</strong> placeholder will be replaced with their name.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => regenerateScript(0)}
                      disabled={isGenerating || isBatchGenerating}
                      className="btn-regenerate"
                      title={hasCampaignMessageBody(scripts[0], { bodyFieldLabels: askFields }) ? "Regenerate this message with AI" : "Generate a message with AI"}
                    >
                      {isGenerating && generatingIndex === 0 ? (
                        <>
                          <RefreshCw size={14} className="spinning" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <RefreshCw size={14} />
                          {hasCampaignMessageBody(scripts[0], { bodyFieldLabels: askFields }) ? 'Regenerate' : 'Generate'}
                        </>
                      )}
                    </button>
                  </div>
                  <div className={`email-preview-container ${isBatchGenerating ? 'email-preview-container--regenerating' : ''}`}>
                    {originalScripts.current[0] && (
                      <div className="script-actions">
                        <button
                          type="button"
                          className="script-reset-btn"
                          onClick={() => resetScriptToOriginal(0)}
                          disabled={isBatchGenerating || !isScriptModified(0)}
                          title="Reset to uploaded message"
                        >
                          <RefreshCw size={12} />
                          Reset to Uploaded
                        </button>
                        <span className="script-reset-hint">Restore the original message from your upload</span>
                      </div>
                    )}
                    <HighlightedTextarea
                      textareaRef={el => scriptRefs.current[0] = el}
                      value={scripts[0] || ''}
                      onChange={(e) => handleScriptChange(0, e.target.value)}
                      onInput={fitAllTextareas}
                      onKeyDown={(e) => protectPlaceholder(e, 0)}
                      className="email-preview-textarea"
                      placeholder="Enter your email message. SiteSift adds the greeting and your signature."
                      disabled={isBatchGenerating}
                    />
                    <SignaturePreview
                        signatureMode={signatureMode}
                        emailSignature={emailSignature}
                        isLoading={isLoadingProfile}
                        onSettingsClick={goToSettings}
                      />
                  </div>
                </div>

                {/* Regenerate All Section - show when campaign prepared and multiple scripts */}
                {campaignPrepared && neededScriptCount > 1 && !isBatchGenerating && (
                  <div className="regenerate-all-section">
                    <div className="regenerate-all-content">
                      <div className="regenerate-all-info">
                        <RefreshCw size={18} className="regenerate-all-icon" />
                        <div>
                          <h4 className="regenerate-all-title">Sync Secondary Emails</h4>
                          <p className="regenerate-all-description">
                            Made changes to your primary message? Regenerate all {neededScriptCount - 1} secondary email{neededScriptCount > 2 ? 's' : ''} to match your updated content and tone.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={regenerateAllSecondaryScripts}
                        disabled={isGenerating || isBatchGenerating || !hasCampaignMessageBody(scripts[0], { bodyFieldLabels: askFields })}
                        className="btn-regenerate-all"
                      >
                        <RefreshCw size={14} />
                        Regenerate All ({neededScriptCount - 1})
                      </button>
                    </div>
                  </div>
                )}

                {/* Follow-up Scripts - only show if needed */}
                {neededScriptCount > 1 && scripts.slice(1, neededScriptCount).map((script, i) => {
                  const index = i + 1;
                  const isThisGenerating = isGenerating && generatingIndex === index;
                  return (
                    <div key={index} className="script-section script-section--followup">
                      <div className="script-header">
                        <div className="script-header-info">
                          <Mail className="script-header-icon" />
                          <div>
                            <h3 className="script-title">{getScriptLabel(index)}</h3>
                            <p className="script-description">
                              Sent when emailing the same contact about their {index === 1 ? 'second' : `${index + 1}${getOrdinalSuffix(index + 1)}`} property. Includes a transition acknowledging prior emails.
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => regenerateScript(index)}
                          disabled={isGenerating || isBatchGenerating || !hasCampaignMessageBody(scripts[0], { bodyFieldLabels: askFields })}
                          className="btn-regenerate"
                          title="Regenerate this message with AI"
                        >
                          {isThisGenerating ? (
                            <>
                              <RefreshCw size={14} className="spinning" />
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <RefreshCw size={14} />
                              Regenerate
                            </>
                          )}
                        </button>
                      </div>
                      <div className={`email-preview-container ${isBatchGenerating ? 'email-preview-container--regenerating' : ''}`}>
                        {originalScripts.current[index] && (
                          <div className="script-actions">
                            <button
                              type="button"
                              className="script-reset-btn"
                              onClick={() => resetScriptToOriginal(index)}
                              disabled={isBatchGenerating || !isScriptModified(index)}
                              title="Reset to uploaded message"
                            >
                              <RefreshCw size={12} />
                              Reset to Uploaded
                            </button>
                            <span className="script-reset-hint">Restore the original message from your upload</span>
                          </div>
                        )}
                        <HighlightedTextarea
                          textareaRef={el => scriptRefs.current[index] = el}
                          value={script || ''}
                          onChange={(e) => handleScriptChange(index, e.target.value)}
                          onInput={fitAllTextareas}
                          onKeyDown={(e) => protectPlaceholder(e, index)}
                          className="email-preview-textarea"
                          placeholder={isThisGenerating ? 'Generating...' : `Enter your ${index + 1}${getOrdinalSuffix(index + 1)} contact message. SiteSift adds the greeting and your signature.`}
                          disabled={isThisGenerating}
                        />
                        <SignaturePreview
                            signatureMode={signatureMode}
                            emailSignature={emailSignature}
                            isLoading={isLoadingProfile}
                            onSettingsClick={goToSettings}
                          />
                      </div>
                    </div>
                  );
                })}

                {/* Batch generating indicator - shown below scripts */}
                {isBatchGenerating && (
                  <div className="batch-generating-notice">
                    <RefreshCw size={16} className="spinning" />
                    <span>Generating follow-up messages...</span>
                  </div>
                )}

                {/* Recipients Section - only show after campaign is prepared */}
                {campaignPrepared && (
                <div className="recipients-section">
                  <div className="recipients-header">
                    <Users className="recipients-icon" />
                    <h4 className="recipients-title">Recipients</h4>
                    <span className="recipients-count">
                      {uniqueContactCount} contact{uniqueContactCount !== 1 ? 's' : ''}, {totalPropertyCount} propert{totalPropertyCount !== 1 ? 'ies' : 'y'}
                    </span>
                  </div>
                  <section
                    className={`recipient-variable-review ${recipientVariableIssueCount > 0 || variableReviewError ? 'recipient-variable-review--error' : 'recipient-variable-review--ready'}`}
                    aria-live="polite"
                  >
                    <div className="recipient-variable-review__icon">
                      <AlertCircle size={18} />
                    </div>
                    <div className="recipient-variable-review__body">
                      <div className="recipient-variable-review__title">
                        Confirm recipient identity before launch
                      </div>
                      {recipientVariableIssueCount > 0 ? (
                        <p>
                          Fix {recipientVariableIssueCount} row{recipientVariableIssueCount !== 1 ? 's' : ''} before launch.
                          {recipientContactResolutionIssueCount > 0 && (
                            <> Shared mailboxes with multiple known contacts need a full Leasing Contact on every row; a greeting override does not resolve recipient identity.</>
                          )}
                          {recipientNameIssueCount > 0 && (
                            <> Messages using <strong>[NAME]</strong> also need a confirmed greeting name.</>
                          )}
                        </p>
                      ) : (
                        <p>
                          {recipientVariableReadyCount} recipient row{recipientVariableReadyCount !== 1 ? 's' : ''} ready.
                          Every <strong>[NAME]</strong> placeholder has a confirmed first name.
                        </p>
                      )}
                    </div>
                  </section>
                  <div className="broker-list">
                    {Object.entries(brokerData).map(([email, properties]) => (
                      <div key={email} className={`broker-card ${isAnimating ? 'sending' : ''}`}>
                        <div className="broker-header">
                          <div className="broker-icon-wrapper">
                            <Mail className="broker-icon" />
                          </div>
                          <div className="broker-info">
                            <div className="broker-email">{email}</div>
                            <div className="broker-contact">
                              {recipientContactLabelByEmail.get(email) || 'Unknown contact'}
                            </div>
                          </div>
                          <span className="broker-property-count">
                            {properties.length} propert{properties.length !== 1 ? 'ies' : 'y'}
                          </span>
                          {!isAnimating && (
                            <button
                              onClick={() => removeBroker(email)}
                              className="btn-remove-broker"
                              title="Remove broker"
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                        <div className="property-list">
                          {properties.map((property, propIndex) => {
                            // Build display: Address, City
                            const addressParts = [];
                            if (property.address) addressParts.push(property.address);
                            if (property.city) addressParts.push(property.city);
                            const addressText = addressParts.length > 0
                              ? addressParts.join(', ')
                              : `Property ${propIndex + 1}`;

                            // Get contact name for this property
                            const variableReview = recipientVariableReviewByKey[`${email}-${propIndex}`];
                            const contactName = property.contact || '';
                            const firstName = variableReview?.injectionFirstName || '';
                            const messageIndex = variableReview?.scriptIndex ?? propIndex;
                            const hasVariableIssue = Boolean(variableReview?.isBlocking);
                            const isEditingThis = editingFirstName?.email === email && editingFirstName?.propIndex === propIndex;

                            return (
                              <div
                                key={`${email}-${propIndex}`}
                                className={`property-item property-item--detailed ${hasVariableIssue ? 'property-item--variable-error' : ''}`}
                                data-variable-review-issue={hasVariableIssue ? 'true' : undefined}
                              >
                                <MapPin size={14} className="property-icon" />
                                <div className="property-info">
                                  <span className="property-address">
                                    {addressText}
                                    {property.propertyName && <span className="property-name"> ({property.propertyName})</span>}
                                  </span>
                                  <div className="property-name-row">
                                    <span className="property-script-label">
                                      {messageIndex === 0 ? 'Primary' : `${messageIndex + 1}${getOrdinalSuffix(messageIndex + 1)} contact`} message, [NAME] =
                                    </span>
                                    {isEditingThis ? (
                                      <div className="property-firstname-edit">
                                        <input
                                          type="text"
                                          value={editingFirstNameValue}
                                          onChange={(e) => setEditingFirstNameValue(e.target.value)}
                                          onFocus={(e) => {
                                            if (!firstNameSuggestionPendingRef.current) return;
                                            firstNameSuggestionPendingRef.current = false;
                                            e.target.select();
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') saveEditingFirstName();
                                            if (e.key === 'Escape') setEditingFirstName(null);
                                          }}
                                          className="property-firstname-input"
                                          autoFocus
                                          placeholder="First name..."
                                        />
                                        <button
                                          onClick={saveEditingFirstName}
                                          className="btn-save-contact"
                                          title="Save"
                                        >
                                          <Check size={12} />
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="property-firstname-display">
                                        <span className={firstName ? 'property-firstname-value' : 'property-contact-empty property-contact-empty--blocking'}>
                                          {firstName || 'not set'}
                                        </span>
                                        {!isAnimating && (
                                          <button
                                            onClick={() => startEditingFirstName(
                                              email,
                                              propIndex,
                                              firstName,
                                              variableReview?.injectionSource !== 'manual'
                                            )}
                                            className="btn-edit-contact"
                                            title="Edit name used in email"
                                          >
                                            <Pencil size={12} />
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="property-contact-row">
                                    <span className="property-contact-label">Leasing Contact:</span>
                                    <span className="property-contact-value">{contactName || 'Not set'}</span>
                                  </div>
                                  {hasVariableIssue && (
                                    <div className="property-variable-warning" role="alert">
                                      {variableReview?.requiresContactResolution
                                        ? "Set this row's full Leasing Contact before launch. A greeting override cannot choose between multiple contacts sharing this mailbox."
                                        : 'Add a first name before launch. Otherwise this row could send with an unresolved name placeholder.'}
                                    </div>
                                  )}
                                </div>
                                {!isAnimating && properties.length > 1 && (
                                  <button
                                    onClick={() => removeProperty(email, propIndex)}
                                    className="btn-remove-property"
                                    title="Remove property"
                                  >
                                    <X size={12} />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* Follow-Up Settings Section - only show after campaign is prepared */}
                {campaignPrepared && (
                <div className="followup-settings-section">
                  <div className="followup-header">
                    <Clock className="followup-header-icon" />
                    <div>
                      <h4 className="followup-title">Follow-Up Sequence</h4>
                      <p className="followup-description">
                        Automatically send follow-ups if brokers don't respond
                      </p>
                    </div>
                  </div>

                  <div className="followup-count-selector">
                    <span className="followup-count-label">Number of follow-ups:</span>
                    <div className="followup-count-buttons">
                      {[0, 1, 2, 3].map(count => (
                        <button
                          key={count}
                          className={`followup-count-btn ${followUpCount === count ? 'active' : ''}`}
                          onClick={() => setFollowUpCount(count)}
                          disabled={isAnimating}
                        >
                          {count}
                        </button>
                      ))}
                    </div>
                  </div>

                  {followUpCount > 0 && (
                    <div className="followup-list">
                      {followUps.slice(0, followUpCount).map((followUp, index) => {
                        // Calculate cumulative time for display
                        const getCumulativeTime = () => {
                          let totalMinutes = 0;
                          for (let i = 0; i <= index; i++) {
                            const fu = followUps[i];
                            const time = fu.waitTime || 1;
                            const unit = fu.waitUnit || 'days';
                            if (unit === 'minutes') totalMinutes += time;
                            else if (unit === 'hours') totalMinutes += time * 60;
                            else totalMinutes += time * 24 * 60;
                          }
                          if (totalMinutes < 60) return `${totalMinutes} minute${totalMinutes !== 1 ? 's' : ''}`;
                          if (totalMinutes < 1440) {
                            const hours = (totalMinutes / 60).toFixed(1).replace(/\.0$/, '');
                            return `${hours} hour${hours !== '1' ? 's' : ''}`;
                          }
                          const days = (totalMinutes / 1440).toFixed(1).replace(/\.0$/, '');
                          return `${days} day${days !== '1' ? 's' : ''}`;
                        };

                        return (
                        <div key={index} className="followup-item">
                          <div className="followup-item-header">
                            <span className="followup-item-label">
                              Follow-up #{index + 1}
                              {index === followUpCount - 1 && ' (final)'}
                            </span>
                            <div className="followup-time-selector">
                              <span className="followup-wait-label">{index === 0 ? 'Wait:' : 'Then wait:'}</span>
                              <select
                                value={followUp.waitTime}
                                onChange={(e) => updateFollowUp(index, 'waitTime', parseInt(e.target.value))}
                                className="followup-time-select"
                                disabled={isAnimating}
                              >
                                {followUp.waitUnit === 'hours' ? (
                                  <>
                                    <option value={4}>4</option>
                                    <option value={6}>6</option>
                                    <option value={8}>8</option>
                                    <option value={12}>12</option>
                                    <option value={24}>24</option>
                                    <option value={48}>48</option>
                                  </>
                                ) : (
                                  <>
                                    <option value={1}>1</option>
                                    <option value={2}>2</option>
                                    <option value={3}>3</option>
                                    <option value={5}>5</option>
                                    <option value={7}>7</option>
                                    <option value={10}>10</option>
                                    <option value={14}>14</option>
                                  </>
                                )}
                              </select>
                              <select
                                value={followUp.waitUnit}
                                onChange={(e) => {
                                  const newUnit = e.target.value;
                                  // Realistic follow-up intervals: hours (4, 8, 24) or days (5, 3, 2)
                                  const defaultTime = newUnit === 'hours'
                                    ? (index === 0 ? 4 : index === 1 ? 8 : 24)
                                    : (index === 0 ? 5 : index === 1 ? 3 : 2);
                                  updateFollowUp(index, 'waitUnit', newUnit);
                                  updateFollowUp(index, 'waitTime', defaultTime);
                                }}
                                className="followup-unit-select"
                                disabled={isAnimating}
                              >
                                <option value="hours">hours</option>
                                <option value="days">days</option>
                              </select>
                              {index > 0 && <span className="followup-cumulative-hint">({getCumulativeTime()} total)</span>}
                            </div>
                          </div>
                          <div className="followup-message-container">
                            <div className="followup-actions">
                              <button
                                type="button"
                                className="followup-reset-btn"
                                onClick={() => resetFollowUpMessage(index)}
                                disabled={isAnimating}
                                title="Reset to default message"
                              >
                                <RefreshCw size={12} />
                                Reset to Default
                              </button>
                              <span className="followup-reset-hint">Left unchanged, SiteSift sends its standard follow-up (reworded if the broker has already replied)</span>
                            </div>
                            <HighlightedTextarea
                              textareaRef={el => followUpRefs.current[index] = el}
                              value={followUp.message || DEFAULT_FOLLOWUP_MESSAGES[index]}
                              onChange={(e) => {
                                updateFollowUp(index, 'message', e.target.value);
                              }}
                              className="followup-message-textarea"
                              placeholder={`Enter follow-up #${index + 1} message...`}
                              disabled={isAnimating}
                            />
                          </div>
                          {(signatureMode === 'professional' || (signatureMode === 'custom' && emailSignature)) && (
                            <div className="followup-signature">
                              <SignaturePreview
                                signatureMode={signatureMode}
                                emailSignature={emailSignature}
                                isLoading={false}
                                compact={true}
                              />
                            </div>
                          )}
                        </div>
                      );
                      })}

                      <div className="followup-info-note">
                        <AlertCircle size={14} />
                        <span>
                          Follow-ups pause when a broker responds. If they go silent again, the sequence resumes from where it left off.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Scroll Indicator */}
              {showScrollIndicator && !isAnimating && (
                <div
                  className="scroll-indicator"
                  onClick={() => {
                    if (modalContainerRef.current) {
                      modalContainerRef.current.scrollTo({
                        top: modalContainerRef.current.scrollHeight,
                        behavior: 'smooth'
                      });
                    }
                  }}
                >
                  <ChevronDown className="scroll-indicator-icon" size={24} />
                </div>
              )}

              {/* Actions */}
              <div className="modal-actions">
                {/* Send mode — PARKED: combined-per-broker gated off live (see backlog) */}
                {COMBINE_MODE_ENABLED && Object.values(brokerData).some(props => props.length > 1) && (
                  <div className="send-mode-toggle">
                    <span className="send-mode-label">When a broker has multiple properties</span>
                    <div className="send-mode-switch" data-mode={sendMode} role="group" aria-label="Send mode">
                      <span className="send-mode-switch-thumb" aria-hidden="true" />
                      <button
                        type="button"
                        className="send-mode-seg"
                        aria-pressed={sendMode === 'separate'}
                        onClick={() => handleSendModeChange('separate')}
                        disabled={isAnimating || isBatchGenerating}
                      >
                        Separate emails
                      </button>
                      <button
                        type="button"
                        className="send-mode-seg"
                        aria-pressed={sendMode === 'combined'}
                        onClick={() => handleSendModeChange('combined')}
                        disabled={isAnimating || isBatchGenerating}
                      >
                        One combined email
                      </button>
                    </div>
                    {campaignPrepared && (
                      <span className="send-mode-hint">
                        {sendMode === 'combined'
                          ? 'Multi-property brokers get one email. Switch back anytime — it re-prepares.'
                          : 'Each property sends separately. Switch anytime — it re-prepares.'}
                      </span>
                    )}
                  </div>
                )}
                {/* Show stagger note if any broker has multiple properties */}
                {Object.values(brokerData).some(props => props.length > 1) && campaignPrepared && (
                  <p className="stagger-note">
                    Each property will be sent as a separate email thread to keep things organized and easy to track. Emails to the same contact will be staggered 2 minutes apart.
                  </p>
                )}
                {/* Show prepare info when scripts need to be generated */}
                {!campaignPrepared && hasCampaignMessageBody(scripts[0], { bodyFieldLabels: askFields }) && (
                  <p className="prepare-note">
                    Click "Prepare Campaign" to generate personalized emails for each contact based on your message above.
                  </p>
                )}
                <div className="modal-actions-buttons">
                  <button
                    className="btn-cancel"
                    disabled={isAnimating || isBatchGenerating}
                    onClick={onClose}
                  >
                    {embedded ? 'Back' : 'Cancel'}
                  </button>
                  {!campaignPrepared ? (
                    // Show "Prepare Campaign" button when scripts need generating
                    <button
                      onClick={prepareCampaign}
                      disabled={isBatchGenerating || !hasCampaignMessageBody(scripts[0], { bodyFieldLabels: askFields }) || Object.keys(brokerData).length === 0}
                      className={`btn-confirm btn-prepare ${isBatchGenerating ? 'btn-animating' : ''}`}
                    >
                      {isBatchGenerating ? (
                        <>
                          Preparing...
                          <span className="spinner-inline" />
                        </>
                      ) : (
                        <>
                          <RefreshCw className="btn-icon" />
                          Prepare Campaign
                        </>
                      )}
                    </button>
                  ) : (
                    // Show "Send Emails" button when campaign is ready
                    <button
                      onClick={handleConfirm}
                      disabled={isAnimating || isGenerating || isBatchGenerating || launchBlockers.length > 0}
                      aria-describedby={launchBlockers.length > 0 ? 'campaign-launch-blockers' : undefined}
                      className={`btn-confirm ${isAnimating ? 'btn-animating' : ''}`}
                    >
                      {isAnimating ? (
                        <>
                          {embedded ? 'Starting...' : 'Sending...'}
                          <span className="spinner-inline" />
                        </>
                      ) : (
                        <>
                          <Send className="btn-icon" />
                          {embedded ? 'Start Campaign' : 'Send Emails'}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );

  if (embedded) {
    return modalContent;
  }

  return createPortal(
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isAnimating) onClose();
      }}
    >
      {modalContent}
    </div>,
    document.body
  );
}
